import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { io } from 'socket.io-client';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import {
  Columns,
  Search,
  Filter,
  Plus,
  MoreVertical,
  Clock,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Play,
  Calendar,
  Layers,
  ChevronRight,
  ChevronDown,
  X,
  User,
  Zap,
  Edit3,
  Check,
  RotateCcw,
  MessageSquare,
  Maximize2,
  Info,
  ListTodo,
  MinusCircle,
  Bug,
  Globe,
  Activity,
  Copy,
  Link as LinkIcon,
  ExternalLink,
  Camera,
  Lock,
  ArrowRight,
  Database,
  RefreshCw,
  Pencil,
  Trash2,
  Quote,
  Monitor,
  Bot,
  Sparkles,
  Waypoints,
  ListTree
} from 'lucide-react';
import api from '../services/api';
import EmptyState from '../components/EmptyState';
import { motion, Reorder, AnimatePresence, useInView } from 'framer-motion';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useLanguage } from '../context/LanguageContext';
import { useProject } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import CustomDropdown from '../components/CustomDropdown';
import FilterModal from '../components/FilterModal';
import Tooltip from '../components/Tooltip';
import ScrumTrafficLight from '../components/ScrumTrafficLight';
import { calculateSprintHealth } from '../utils/scrumUtils';

const priorityOrder = {
  urgent: 4,
  high: 3,
  medium: 2,
  low: 1
};

const PRIORITY_STYLES = {
  urgent: {
    bg: 'rgba(239, 68, 68, 0.15)',
    text: '#ef4444',
    border: 'rgba(239, 68, 68, 0.3)',
    bgSubtle: 'rgba(239, 68, 68, 0.05)',
    borderSubtle: 'rgba(239, 68, 68, 0.1)',
    textSubtle: '#fca5a5'
  },
  high: {
    bg: 'rgba(245, 158, 11, 0.15)',
    text: '#f59e0b',
    border: 'rgba(245, 158, 11, 0.3)',
    bgSubtle: 'rgba(245, 158, 11, 0.05)',
    borderSubtle: 'rgba(245, 158, 11, 0.1)',
    textSubtle: '#fdba74'
  },
  medium: {
    bg: 'rgba(99, 102, 241, 0.15)',
    text: '#6366f1',
    border: 'rgba(99, 102, 241, 0.3)',
    bgSubtle: 'rgba(99, 102, 241, 0.05)',
    borderSubtle: 'rgba(99, 102, 241, 0.1)',
    textSubtle: '#a5b4fc'
  },
  low: {
    bg: 'rgba(16, 185, 129, 0.15)',
    text: '#10b981',
    border: 'rgba(16, 185, 129, 0.3)',
    bgSubtle: 'rgba(16, 185, 129, 0.05)',
    borderSubtle: 'rgba(16, 185, 129, 0.1)',
    textSubtle: '#6ee7b7'
  }
};

const DEFAULT_PRIORITY_STYLE = PRIORITY_STYLES.medium;

const getPriorityStyles = (priority) => {
  return PRIORITY_STYLES[priority] || DEFAULT_PRIORITY_STYLE;
};

const get_status_columns = (t) => [
  { id: 'todo', label: t('todo'), color: 'amber' },
  { id: 'bug_fixing', label: t('bug_fixing'), color: 'rose' },
  { id: 'review', label: t('review'), color: 'purple' },
  { id: 'done', label: t('done'), color: 'emerald' },
];

const getResolverStatusLabel = (ticket, t) => {
  if (ticket.status === 'in_progress' || ticket.status === 'bug_fixing') return t('takenBy');
  if (ticket.status === 'applied') return t('appliedBy');
  if (ticket.status === 'ready_for_review' || ticket.status === 'review') return t('deployedBy');
  if (ticket.status === 'completed' || ticket.status === 'done') return t('completedBy');
  return t('resolvedLabel');
};

/**
 * REPLICATION OF BACKEND getKanbanFilters LOGIC (server.js:3672)
 * Ensures frontend real-time updates match backend partitioning
 */
const isTicketInColumn = (ticket, columnId, showUiImprovements = false, hasUxMembers = true) => {
  if (!ticket) return false;

  // Exclusive filtering: If showing UI Improvements, only include items with isUiImprovement flag
  if (showUiImprovements) {
    if (!hasUxMembers || ticket.isUiImprovement !== true) return false;
  } else {
    // Only exclude UI items if there are dedicated UX/UI members to handle them
    if (hasUxMembers && ticket.isUiImprovement === true) return false;
  }

  
  const terminalStatuses = ['done', 'completed', 'resolved', 'live'];
  const reviewStatuses = ['review', 'ready_for_review'];

  const isReview = reviewStatuses.includes(ticket.status) || ticket.boardColumn === 'review';
  const isDone = terminalStatuses.includes(ticket.status) || ticket.boardColumn === 'done';
  const isBug = (ticket.status === 'bug_fixing' || ticket.originalStatus === 'bug_fixing' || ticket.type === 'bug') && !isReview && !isDone;

  if (columnId === 'review') return isReview;
  if (columnId === 'done') return isDone;
  if (columnId === 'bug_fixing') return isBug;
  if (columnId === 'todo') return !isReview && !isDone && !isBug;

  return false;
};


const BorderBeam = React.memo(({ color, borderRadius = 16, isVisible = true }) => {
  if (!isVisible) return null;
  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-hidden" style={{ borderRadius }}>
      <motion.rect
        x="0.5"
        y="0.5"
        width="calc(100% - 1px)"
        height="calc(100% - 1px)"
        rx={borderRadius}
        ry={borderRadius}
        fill="transparent"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        style={{ filter: `drop-shadow(0 0 4px ${color})` }}
        initial={{ pathLength: 1, strokeDasharray: "0 1", strokeDashoffset: 0 }}
        animate={{
          strokeDasharray: "0.08 0.92", // 8% snake length
          strokeDashoffset: [0, -1]    // Offset travels full path
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "linear"
        }}
      />
    </svg>
  );
});

const FootprintIcon = ({ color }) => (
  <div className="flex flex-col gap-1 items-center">
    {/* Front Legs - Closer and smaller */}
    <div className="flex justify-between w-[20px]">
      <div className="w-[1.2px] h-[2.2px] rounded-full blur-[0.2px]" style={{ backgroundColor: color, boxShadow: `0 0 5px ${color}` }} />
      <div className="w-[1.2px] h-[2.2px] rounded-full blur-[0.2px]" style={{ backgroundColor: color, boxShadow: `0 0 5px ${color}` }} />
    </div>
    {/* Middle Legs - Wider and more prominent */}
    <div className="flex justify-between w-[25px]">
      <div className="w-[1.8px] h-[2.8px] rounded-full blur-[0.3px]" style={{ backgroundColor: color, boxShadow: `0 0 8px ${color}` }} />
      <div className="w-[1.8px] h-[2.8px] rounded-full blur-[0.3px]" style={{ backgroundColor: color, boxShadow: `0 0 8px ${color}` }} />
    </div>
    {/* Back Legs - Similar to front but slightly different */}
    <div className="flex justify-between w-[22px]">
      <div className="w-[1.5px] h-[2.5px] rounded-full blur-[0.2px]" style={{ backgroundColor: color, boxShadow: `0 0 5px ${color}` }} />
      <div className="w-[1.5px] h-[2.5px] rounded-full blur-[0.2px]" style={{ backgroundColor: color, boxShadow: `0 0 5px ${color}` }} />
    </div>
  </div>
);

const WalkingBugStyle = () => (
  <style>{`
    @keyframes bugBodyJitter {
      0% { transform: translate(0, 0); }
      50% { transform: translate(0.5px, 0.5px); }
      100% { transform: translate(0, 0); }
    }

    /* Organic leg movement: Staggered */
    @keyframes bugLegLeft {
      0%   { transform: rotate(0deg); }
      33%  { transform: rotate(18deg); }
      66%  { transform: rotate(-12deg); }
      100% { transform: rotate(0deg); }
    }

    @keyframes bugLegRight {
      0%   { transform: rotate(0deg); }
      33%  { transform: rotate(-18deg); }
      66%  { transform: rotate(12deg); }
      100% { transform: rotate(0deg); }
    }

    .bug-moving {
      animation: bugBodyJitter 0.15s infinite steps(2);
    }

    .bug-moving .legs-left {
      animation: bugLegLeft 0.25s infinite ease-in-out;
    }

    .bug-moving .legs-right {
      animation: bugLegRight 0.25s infinite ease-in-out;
    }
    
    .bug-moving .legs-left-mid {
       animation: bugLegRight 0.25s infinite ease-in-out;
       animation-delay: 0.05s;
    }
    
    .bug-moving .legs-right-mid {
       animation: bugLegLeft 0.25s infinite ease-in-out;
       animation-delay: 0.05s;
    }
  `}</style>
);

const WalkingBugBody = ({ size = 16, color, isMoving }) => {
  return (
    <svg
      className={isMoving ? 'bug-moving' : ''}
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <WalkingBugStyle />

      {/* antenas */}
      <path d="m8 2 1.88 1.88" />
      <path d="M14.12 3.88 16 2" />

      {/* cabeza */}
      <path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1" />

      {/* cuerpo */}
      <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6" fill="currentColor" fillOpacity={0.15} />
      <path d="M12 20v-9" />

      {/* patas izquierda - Staggered */}
      <g className="legs-left" style={{ transformOrigin: 'center' }}>
        <path d="M6.53 9C4.6 8.8 3 7.1 3 5" />
        <path d="M3 21c0-2.1 1.7-3.9 3.8-4" />
      </g>
      <g className="legs-left-mid" style={{ transformOrigin: 'center' }}>
        <path d="M6 13H2" />
      </g>

      {/* patas derecha - Staggered */}
      <g className="legs-right" style={{ transformOrigin: 'center' }}>
        <path d="M20.97 5c0 2.1-1.6 3.8-3.5 4" />
        <path d="M17.2 17c2.1.1 3.8 1.9 3.8 4" />
      </g>
      <g className="legs-right-mid" style={{ transformOrigin: 'center' }}>
        <path d="M22 13h-4" />
      </g>
    </svg>
  );
};

const WalkingBug = ({ isHovered, activeProject, collisionRegistry }) => {
  const { lang: language } = useLanguage();
  const { addToast } = useToast();
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [isReturning, setIsReturning] = useState(false);
  const [footprints, setFootprints] = useState([]);
  const [bugColor, setBugColor] = useState('#f43f5e');
  const [isActuallyWalking, setIsActuallyWalking] = useState(false);
  const [returnDuration, setReturnDuration] = useState(1.5);
  const [walkInterval, setWalkInterval] = useState(150);
  const [message, setMessage] = useState('');
  const [showBubble, setShowBubble] = useState(false);
  const [actuallyHovered, setActuallyHovered] = useState(false);
  const [currentDay, setCurrentDay] = useState(new Date().getDay());

  useEffect(() => {
    let timeout;
    if (isHovered) {
      timeout = setTimeout(() => {
        setActuallyHovered(true);
      }, 2000);
    } else {
      setActuallyHovered(false);
      if (timeout) clearTimeout(timeout);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isHovered]);
  const lastPos = useRef({ x: 0, y: 0 });
  const lastPhraseRef = useRef('');

  useEffect(() => {
    // Check for day changes every minute to be safe
    const interval = setInterval(() => {
      const d = new Date().getDay();
      if (d !== currentDay) setCurrentDay(d);
    }, 60000);
    return () => clearInterval(interval);
  }, [currentDay]);

  const teamMembersByRole = useMemo(() => {
    const roles = { qa: [], dev: [], ux: [], pm: [], backend: [] };
    if (!activeProject?.members) return roles;

    activeProject.members.forEach(m => {
      const name = m.name?.split(' ')[0] || 'Alguien';
      const role = m.role?.toLowerCase() || '';

      if (role.includes('qa') || role.includes('tester')) roles.qa.push(name);
      else if (role.includes('ux') || role.includes('ui') || role.includes('design')) roles.ux.push(name);
      else if (role.includes('pm') || role.includes('lead') || role.includes('manager')) roles.pm.push(name);
      else if (role.includes('backend') || role.includes('api') || role.includes('node') || role.includes('server')) roles.backend.push(name);
      else roles.dev.push(name); // Default to frontend/dev
    });
    return roles;
  }, [activeProject]);

  const PHRASES = useMemo(() => ({
    es: {
      walking: {
        generic: [
          "¡Esto no es un bug, es una feature!",
          "¿Deploy en viernes? Qué valientes...",
          "Ignórame, solo estoy debugueando en vivo",
          "Consola limpia, conciencia sucia",
          "Buscando el punto y coma que falta...",
          "¡No me pises, que muerdo el código!",
          "Soy el bicho más famoso de ViewGate",
          "¡Funciona en mi máquina!",
          "¿Quién necesita tests unitarios?",
          "Refactorizar es de cobardes",
          "Un console.log y a producción",
          "¿Stack Overflow está caído?",
          "Café convertido en código... y bugs",
          "Git push --force y a dormir",
          "¿Me ves cara de ticket de Jira?",
          "El código se explica solo... o no",
          "¡Socorro, me persigue un linter!",
          "No soy un error, soy un detalle imprevisto",
          "Mi lógica es impecable, el entorno no",
          "¿Ya probaron borrando la caché?",
          "¡En producción no pasaba esto!",
          "¡Es culpa de la API!",
          "¿Quién hizo este commit a las 3 AM?",
          "Soy un bug artesanal, hecho a mano",
          "Mi KPI es volver locos a los devs",
          "¿Alguien ha visto mi debugger?",
          "¡Help! ¡Estoy atrapado en una simulación!",
          "Si la vida te da bugs, haz un patch",
          "Buscando al culpable en el git blame...",
          "Soy un bicho de alta fidelidad",
          "Todo es risas hasta que production cae",
          "¡Error 404: Humor no encontrado!",
          "Mi lenguaje favorito es el sarcasmo... y JS",
          "¡Aviso! No alimentar con código legacy",
          "¿Un café? El código no se va a romper solo",
          "¡Cuidado con ese console.table!",
          "Soy el easter egg que nadie pidió",
          "Mi complejidad ciclomática te asustaría",
          "¿Me das una estrella en GitHub?",
          "¡Cuidado, muerdo si el build falla!",
          "¡Alerta de bug! ¡Sálvese quien pueda!",
          "¿Quién necesita café con este código?",
          "Mi religión me prohíbe los side-effects",
          "¡Error de capa 8 detectado!",
          "¿Me invitas a un pull request?",
          "Soy el primer bug con conciencia de clase",
          "¡Vigilando el coverage desde abajo!",
          "¿Alguien ha visto mi documentación?",
          "Mi vida es un bucle infinito de diversión",
          "¡No me toques el CSS que me despeino!",
          "¿Es este el camino a San Junipero?",
          "¡Debugueando con estilo!",
          "¡No soy un bug, soy un turista!",
          "Buscando el console.log olvidado...",
          "Cuidado, que hackeo el CSS",
          "¡Un div sin cerrar! Mis ojos...",
          "Mi vida por un punto y coma",
          "Soy el bicho que vive en tu terminal",
          "¡Git push y que sea lo que Dios quiera!",
          "No me busques en Jira, no existo",
          "Mi superpoder es romper el build",
          "¡Zas! Otro bug para la colección",
          "¡Cero días sin bugs!",
          "¿Alguien ha visto mi node_modules?",
          "Soy un bicho de alto rendimiento",
          "¡El modo debug es mi hábitat!",
          "No me culpes, es una herencia maligna"
        ],
        smart: [
          { day: 5, phrase: "¿Deploy en viernes? Qué valientes..." },
          { day: 1, phrase: "¡Odio los lunes, especialmente en prod!" },
          { day: 3, phrase: "¡Miercuri-coles! Mitad de semana, mitad de bugs." }
        ],
        backend: [
          "SQL injection? ¡A mí me gusta el picante!",
          "¿Índices en la base de datos? Prefiero vivir al límite.",
          "Un SELECT * y que explote todo.",
          "{name}, el microservicio está en terapia intensiva.",
          "{name}, ¡tu query de 500 líneas me dio hambre!",
          "¡Ese JOIN me dolió hasta a mí!",
          "Cache invalidation? Mi único archienemigo.",
          "Redis está gritando, ¿viste {name}?",
          "¿Migration sin backup? ¡Esa es la actitud, {name}!",
          "¡504 Gateway Timeout! Yo no fui...",
          "Postgres me mira raro cuando paso por acá.",
          "Docker: hoy funciona, mañana es un misterio.",
          "¿Me ves cara de cluster de Kubernetes, {name}?"
        ],
        qa: [
          "Oye {name}, ¡esta no la viste venir!",
          "¿Me reportaste tú, {name}?",
          "A que no me atrapas, {name}",
          "¡{name} me tiene manía!",
          "Dile a los devs que es 'unreproducible', {name}",
          "¿Otro bug más, {name}? ¡Soy irresistible!",
          "¡{name}, no me pongas un bloqueante!",
          "¿Me abres un ticket, {name}?",
          "¿Esta también es 'crítica', {name}?",
          "Oye {name}, ¿has probado a reiniciar?",
          "¡{name}, juro que el bicho es inofensivo!",
          "¿Me pones en el próximo sprint, {name}?",
          "Oye {name}, ¿has visto mi nuevo commit?",
          "¡{name}, no me mandes a la papelera!"
        ],
        ux: [
          "¿Está este bicho en el Figma, {name}?",
          "¡{name}, me falta un poco de padding aquí!",
          "¿Soy pixel-perfect para ti, {name}?",
          "Ponme un degradado bien chulo, {name}",
          "¡{name}, mi sombra está mal calculada!",
          "Demasiado espacio en blanco, ¿no {name}?",
          "¿Este color es un #HEX oficial, {name}?",
          "¿Mi contraste es accesible, {name}?",
          "¡{name}, el kerning de este texto me mata!",
          "Oye {name}, ¿el modo oscuro me favorece?",
          "¡Ponme una microanimación, {name}!",
          "¿Soy responsive para ti, {name}?",
          "¡{name}, este grid no está alineado!"
        ],
        dev: [
          "¡{name}, te juro que ayer funcionaba!",
          "¿Un breakpoint aquí, {name}? ¡Qué feo!",
          "¡{name}, mi complejidad ciclomática es de 1!",
          "Dile al cliente que es 'complicado', {name}",
          "¡{name}, no me hagas un hotfix ahora!",
          "¿Merge conflict? ¡Qué mala suerte, {name}!",
          "¡A borrar node_modules otra vez, {name}!",
          "¡{name}, no uses 'any' en TS, por favor!",
          "Oye {name}, ¿me haces un cherry-pick?",
          "¡Cuidado con ese useEffect, {name}!",
          "¡{name}, me has dejado en el merge conflict!",
          "¿Un rebase o un merge, {name}?"
        ],
        pm: [
          "¿Para cuándo el sprint, {name}?",
          "¡${name}, esto no estaba en el backlog!",
          "Dile al cliente que 'estamos en ello', {name}",
          "¡{name}, necesito una estimación para comer!",
          "¿Prioridad urgente? ¡Camino rápido, {name}!",
          "¡{name}, prometo terminar antes del deadline!",
          "¿Me pones en el 'critical path', {name}?",
          "¡{name}, dame un story point por vibrar!",
          "¿Estamos en el 'definition of done', {name}?"
        ],
        ai: [
          "¿Me ves cara de LLM?",
          "Entrenando mi propio modelo de caos...",
          "Falsa alarma, era solo un alucinamiento",
          "Mi GPU está echando humo",
          "¿Quién necesita a Gemini teniendo a este bicho?",
          "Soy una inteligencia artificial... muy básica",
          "Analizando el sentimiento del backlog: Tristeza",
          "¡Token out of bounds! Me voy al carajo",
          "Prompt engineering: 'Bicho, no rompas nada'",
          "Mi red neuronal dice que este código es turbio",
          "¡Perdí mis gradientes en el camino!",
          "¿Alguien ha visto mi función de activación?",
          "Buscando el mínimo global... ¡Encontré un ticket!",
          "Mi red neuronal está en llamas",
          "¡Dime un prompt bonito y me quedo quieto!",
          "¿Hoy Claude se levantó con el pie izquierdo?",
          "Gemini dice que este código tiene 'sentimientos encontrados'.",
          "Mi GPT local está alucinando con este ticket.",
          "¿Otro 404? Dile a la IA que deje de inventar rutas.",
          "Buscando alucinaciones en el código... ¡Encontré tres!",
          "¡Yo soy el único bicho inteligente aquí!",
          "¿Te imaginas si Claude tuviera mi carisma?",
          "¡Si Gemini viera mis patas, colapsa el servidor!"
        ],
        mcp: [
          "¿Conectando con el MCP? Dame un segundo...",
          "¡Error de timeout en el MCP! Yo me lavo las manos",
          "Mi conexión al MCP está en modo avión",
          "MCP: '¿Quién es ese bicho que camina por mi código?'",
          "¡Bicho vs MCP! La batalla final",
          "¿Qué herramienta del MCP me recomiendas para mis patitas?",
          "El MCP dice que soy un 'feature', no un bug",
          "¡Oops! El MCP alucina más que yo",
          "Sincronizando con el servidor MCP... 1%... 2%... ¡Error!",
          "Mi vida depende de un JSON del MCP"
        ]
      },
      returning: {
        generic: [
          "¡Me voy que hoy hay after!",
          "Se me hace tarde para la daily...",
          "¡Chau! Me vuelvo a mi nido.",
          "Me llaman de producción, ¡adiós!",
          "Falsa alarma, ¡me voy a dormir!",
          "Ya hice suficiente daño por hoy",
          "¡Hasta el próximo deploy!",
          "Me voy a mi rama 'legacy'",
          "¡Cerrando sesión por hoy!",
          "¡Vuelvo cuando suban a staging!",
          "¡Instalando actualizaciones, no apague!",
          "Me voy antes del code review",
          "¡Al infinito y al null!",
          "Me retiro a mi zona de confort (el backlog)",
          "Me voy a mi rama privada",
          "¡Hasta el próximo refactor!",
          "Logout exitoso, ¡nos vemos!",
          "¡Cerrando la terminal por hoy!",
          "¡Adiós, me voy a ver si en staging funciona!",
          "Me retiro, ¡este código me dio sueño!",
          "¡Fuerza bicho! Me voy a hibernar",
          "Me borraron del git ignore, ¡chau!",
          "¡Zarpando hacia localhost:3000!"
        ],
        qa: [
          "¡Nos vemos en el próximo commit, {name}!",
          "¡No me busques más, {name}!"
        ]
      }
    },
    en: {
      walking: {
        generic: [
          "This isn't a bug, it's a feature!",
          "Ignore me, just live debugging",
          "Clean console, dirty conscience",
          "Looking for that missing semicolon...",
          "Don't step on me, I bite the code!",
          "I'm ViewGate's most famous bug",
          "Works on my machine!",
          "Who needs unit tests anyway?",
          "Refactoring is for the weak",
          "One console.log and straight to production",
          "Is Stack Overflow down?",
          "Converting coffee into code... and bugs",
          "Git push --force and off to bed",
          "Do I look like a Jira ticket to you?",
          "The code explains itself... or not",
          "Help! A linter is chasing me!",
          "I'm not an error, I'm an unforeseen detail",
          "My logic is flawless, the environment isn't",
          "Have you tried clearing the cache?",
          "It didn't happen in production!",
          "It's the API's fault!",
          "Who made this commit at 3 AM?",
          "I'm a handcrafted, artisanal bug",
          "My KPI is making devs lose their minds",
          "Has anyone seen my debugger?",
          "Help! I'm trapped in a simulation!",
          "If life gives you bugs, make a patch",
          "Searching for the culprit in git blame...",
          "I'm a high-fidelity bug",
          "It's all fun and games until production goes down",
          "Error 404: Humor not found!",
          "My favorite language is sarcasm... and JS",
          "Warning! Do not feed with legacy code",
          "Coffee? The code won't break itself",
          "Watch out for that console.table!",
          "I'm the easter egg nobody asked for",
          "My cyclomatic complexity would scare you",
          "Can I get a star on GitHub?",
          "Careful, I bite if the build fails!",
          "Bug alert! Every bug for themselves!",
          "Who needs coffee with this code?",
          "My religion forbids side-effects",
          "Layer 8 error detected!",
          "Invite me to a pull request?",
          "I'm the first class-conscious bug",
          "Watching the coverage from below!",
          "Has anyone seen my documentation?",
          "My life is an infinite loop of fun",
          "Don't touch my CSS, you'll mess up my hair!",
          "Is this the way to San Junipero?",
          "Debugging with style!",
          "I'm not a bug, I'm a tourist!",
          "Looking for that forgotten console.log...",
          "Careful, I might hack your CSS",
          "An unclosed div! My eyes...",
          "My life for a semicolon",
          "I'm the bug living in your terminal",
          "Git push and pray!",
          "Don't look for me on Jira, I'm a ghost",
          "My superpower is breaking the build",
          "Boom! Another bug for the collection",
          "Zero days without bugs!",
          "Has anyone seen my node_modules?",
          "I'm a high-performance bug",
          "Debug mode is my habitat!",
          "Don't blame me, it's legacy code"
        ],
        smart: [
          { day: 5, phrase: "Deploying on Friday? How brave..." },
          { day: 1, phrase: "I hate Mondays, especially in production!" },
          { day: 3, phrase: "Hump day! Halfway to the weekend, halfway to more bugs." }
        ],
        backend: [
          "SQL injection? I like it spicy!",
          "Database indices? I prefer living on the edge.",
          "SELECT * and let it all explode.",
          "{name}, the microservice is in intensive care.",
          "{name}, your 500-line query made me hungry!",
          "That JOIN hurt even me!",
          "Cache invalidation? My only archenemy.",
          "Redis is screaming, did you see {name}?",
          "Migration without backup? That's the spirit, {name}!",
          "504 Gateway Timeout! Not me...",
          "Postgres looks at me funny when I walk by.",
          "Docker: works today, tomorrow is a mystery.",
          "Do I look like a Kubernetes cluster to you, {name}?"
        ],
        qa: [
          "Hey {name}, didn't see this one coming!",
          "Did you report me, {name}?",
          "Bet you can't catch me, {name}",
          "{name} has it in for me!",
          "Tell the devs it's 'unreproducible', {name}",
          "Another bug, {name}? I'm irresistible!",
          "{name}, don't give me a blocker!",
          "Opening a ticket for me, {name}?",
          "Is this one 'critical' too, {name}?",
          "Hey {name}, tried a restart?",
          "{name}, I swear I'm harmless!",
          "Put me in the next sprint, {name}?",
          "Hey {name}, seen my new commit?",
          "{name}, don't send me to the trash!"
        ],
        ux: [
          "Is this bug in Figma, {name}?",
          "{name}, I need a bit more padding here!",
          "Am I pixel-perfect to you, {name}?",
          "Give me a cool gradient, {name}",
          "{name}, my shadow is calculated wrong!",
          "Too much whitespace, right {name}?",
          "Is this color an official #HEX, {name}?",
          "Is my contrast accessible, {name}?",
          "{name}, the kerning of this text is killing me!",
          "Hey {name}, does dark mode suit me?",
          "Give me a micro-animation, {name}!",
          "Am I responsive for you, {name}?",
          "{name}, this grid isn't aligned!"
        ],
        dev: [
          "{name}, I swear it worked yesterday!",
          "A breakpoint here, {name}? How rude!",
          "{name}, my cyclomatic complexity is 1!",
          "Tell the client it's 'complicated', {name}",
          "{name}, don't do a hotfix now!",
          "Merge conflict? Tough luck, {name}!",
          "{name}, time to delete node_modules again!",
          "{name}, please don't use 'any' in TS!",
          "Hey {name}, cherry-pick me?",
          "Watch out for that useEffect, {name}!",
          "{name}, you left me in a merge conflict!",
          "Rebase or merge, {name}?"
        ],
        pm: [
          "When's the sprint due, {name}?",
          "{name}, this wasn't in the backlog!",
          "Tell the client 'we're on it', {name}",
          "{name}, I need an estimate to eat!",
          "Urgent priority? I'm walking fast, {name}!",
          "{name}, I promise to finish before the deadline!",
          "Put me in the 'critical path', {name}?",
          "{name}, give me a story point for shaking!",
          "Are we at 'definition of done', {name}?"
        ],
        ai: [
          "Do I look like an LLM to you?",
          "Training my own chaos model...",
          "False alarm, just a hallucination",
          "My GPU is smoking!",
          "Who needs Gemini when you have this bug?",
          "I'm artificial intelligence... very basic",
          "Analyzing backlog sentiment: Sadness",
          "Token out of bounds! I'm outta here",
          "Prompt engineering: 'Bug, don't break anything'",
          "My neural network says this code is fishy",
          "I lost my gradients on the way!",
          "Has anyone seen my activation function?",
          "Searching for global minimum... Found a ticket!",
          "My neural network is on fire!",
          "Give me a nice prompt and I'll stay still!",
          "Is Claude having a bad hair day today?",
          "Gemini says this code has 'mixed feelings'.",
          "My local GPT is hallucinating about this ticket.",
          "Another 404? Tell the AI to stop making up routes.",
          "Searching for hallucinations in the code... Found three!",
          "I'm the only real intelligence here, sorry GPT!",
          "Can you imagine if Claude had my charisma?",
          "If Gemini saw my legs, the server would collapse!",
          "Training my zero-shot chaos model...",
          "Does this code look biased to you? I'm biased towards coffee."
        ],
        mcp: [
          "Connecting to MCP? Give me a sec...",
          "MCP Timeout error! Not my fault",
          "My MCP connection is in airplane mode",
          "MCP: 'Who's that bug walking through my code?'",
          "Bug vs MCP! The final showdown",
          "Which MCP tool do you recommend for my tiny feet?",
          "The MCP says I'm a 'feature', not a bug",
          "Oops! The MCP is hallucinating more than me",
          "Syncing with MCP server... 1%... 2%... Error!",
          "My life depends on an MCP JSON"
        ]
      },
      returning: {
        generic: [
          "Off to the after-party!",
          "Late for the daily standup...",
          "Bye, back to my nest!",
          "Production's calling, gotta go!",
          "False alarm, off to sleep!",
          "Enough damage for today",
          "See you at the next deploy!",
          "Moving to my 'legacy' branch",
          "Signing off for today!",
          "Coming back when you push to staging!",
          "Installing updates, do not turn off!",
          "Leaving before the code review",
          "To infinity and null!",
          "Retiring to my comfort zone (the backlog)",
          "Off to my private branch",
          "See you at the next refactor!",
          "Logout successful, see ya!",
          "Closing the terminal for today!",
          "Bye! Going to see if it works on staging!",
          "I'm out, this code made me sleepy!",
          "Bug speed! I'm going to hibernate",
          "I was deleted from git ignore, bye!",
          "Sailing towards localhost:3000!"
        ],
        qa: [
          "See you at the next commit, {name}!",
          "Don't look for me anymore, {name}!"
        ]
      }
    }
  }), []);

  const WALKING_PHRASES = useMemo(() => {
    const lang = PHRASES[language] || PHRASES.es;
    const pool = [...lang.walking.generic];

    // Day-based smart phrases (reactive state)
    (lang.walking.smart || []).forEach(s => {
      if (s.day === currentDay) pool.push(s.phrase);
    });

    // Backend specific
    teamMembersByRole.backend.forEach(name => {
      (lang.walking.backend || []).forEach(template => {
        pool.push(template.replace('{name}', name));
      });
    });

    // QA
    teamMembersByRole.qa.forEach(name => {
      lang.walking.qa.forEach(template => {
        pool.push(template.replace('{name}', name));
      });
    });

    // UX
    teamMembersByRole.ux.forEach(name => {
      lang.walking.ux.forEach(template => {
        pool.push(template.replace('{name}', name));
      });
    });

    // Dev
    teamMembersByRole.dev.forEach(name => {
      lang.walking.dev.forEach(template => {
        pool.push(template.replace('{name}', name));
      });
    });

    // PM
    teamMembersByRole.pm.forEach(name => {
      lang.walking.pm.forEach(template => {
        pool.push(template.replace('{name}', name));
      });
    });

    // AI Jokes
    if (lang.walking.ai) {
      pool.push(...lang.walking.ai);
    }

    // MCP Jokes
    if (lang.walking.mcp) {
      pool.push(...lang.walking.mcp);
    }

    return pool;
  }, [PHRASES, language, teamMembersByRole]);

  const RETURNING_PHRASES = useMemo(() => {
    const lang = PHRASES[language] || PHRASES.es;
    const pool = [...lang.returning.generic];

    teamMembersByRole.qa.forEach(name => {
      lang.returning.qa.forEach(template => {
        pool.push(template.replace('{name}', name));
      });
    });

    return pool;
  }, [PHRASES, language, teamMembersByRole]);

  const getRandomPhrase = (list) => {
    if (!list || list.length === 0) return '';
    if (list.length === 1) return list[0];

    let newPhrase;
    do {
      newPhrase = list[Math.floor(Math.random() * list.length)];
    } while (newPhrase === lastPhraseRef.current);

    lastPhraseRef.current = newPhrase;
    return newPhrase;
  };

  // Track continuous position for footprints and reactive color
  const currentX = useRef(0);
  const currentY = useRef(0);
  const bugRef = useRef(null);

  const getColumnColor = (x) => {
    // Calibrated for BugStatCard position (~450px from left)
    if (x < -150) return '#f59e0b';    // Todo (Amber)
    if (x < 150) return '#a855f7';    // Review (Purple)
    if (x < 450) return '#f43f5e';    // Bug Fixing (Rose)
    return '#10b981';                  // Done (Emerald)
  };


  useEffect(() => {
    if (!actuallyHovered) {
      // Return to home logic improved
      const deltaX = -currentX.current;
      const deltaY = -currentY.current;

      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      if (distance > 1) {
        setIsReturning(true);
        const retMsg = getRandomPhrase(RETURNING_PHRASES);
        setMessage(retMsg);
        addToast(retMsg, 'bug');
        setShowBubble(true);
        setTimeout(() => setShowBubble(false), 3000);

        const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI) + 90;
        setRotation(angle);

        // Calculate return duration based on distance to be consistent
        const duration = Math.min(2.5, Math.max(1.0, distance / 350));
        setReturnDuration(duration);

        setPos({ x: 0, y: 0 });
        lastPos.current = { x: 0, y: 0 };
      } else {
        setBugColor('#f43f5e');
        setIsActuallyWalking(false);
        setIsReturning(false);
      }
      return;
    }

    setIsReturning(false);

    const move = () => {
      // Range adjusted to visit all columns (from -450px to +750px relative to card)
      const newX = Math.random() * 1600 - 600;
      const newY = Math.random() * 800 + 50;

      const deltaX = newX - lastPos.current.x;
      const deltaY = newY - lastPos.current.y;
      const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI) + 90;

      // Calculate movement speed for animation
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const speed = distance / 2.5; // duration is 2.5s
      setWalkInterval(Math.max(80, Math.min(300, 250 - (speed * 0.4))));

      setRotation(angle);
      setPos({ x: newX, y: newY });
      lastPos.current = { x: newX, y: newY };
    };

    move();
    const interval = setInterval(move, 2800); // Slightly slower intervals for better spacing

    const messageInterval = setInterval(() => {
      if (Math.random() > 0.6) {
        const newMsg = getRandomPhrase(WALKING_PHRASES);
        setMessage(newMsg);
        addToast(newMsg, 'bug');
        setShowBubble(true);
        setTimeout(() => setShowBubble(false), 2500);
      }
    }, 4000);

    return () => {
      clearInterval(interval);
      clearInterval(messageInterval);
    };
  }, [actuallyHovered, WALKING_PHRASES]);

  // Footprint spawning logic
  useEffect(() => {
    if (!actuallyHovered && !isReturning) {
      if (footprints.length > 0) {
        const timeout = setTimeout(() => setFootprints([]), 2000);
        return () => clearTimeout(timeout);
      }
      return;
    }

    const interval = setInterval(() => {
      setFootprints(prev => {
        const newFootprint = {
          id: Date.now(),
          x: currentX.current,
          y: currentY.current,
          rotation,
          color: getColumnColor(currentX.current)
        };
        return [...prev.slice(-18), newFootprint]; // Keep last 18 steps
      });
    }, 120);

    return () => clearInterval(interval);
  }, [isHovered, isReturning, rotation]);

  // Bug walking state control
  useEffect(() => {
    if (!actuallyHovered && !isReturning) {
      setIsActuallyWalking(false);
      return;
    }

    // When we start moving towards a position
    setIsActuallyWalking(true);
  }, [isHovered, isReturning, pos]);

  return (
    <div className="relative pointer-events-none" style={{ position: 'absolute', top: '50%', left: 16 }}>
      {/* Footprints Container */}
      <AnimatePresence>
        {footprints.map(fp => (
          <motion.div
            key={fp.id}
            initial={{ opacity: 0.8, scale: 0.8 }}
            animate={{ opacity: 0, scale: 0.5 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="absolute"
            style={{
              x: fp.x,
              y: fp.y,
              translateX: '-50%',
              translateY: '-50%',
              rotate: fp.rotation,
              zIndex: 90
            }}
          >
            <FootprintIcon color={fp.color} />
          </motion.div>
        ))}
      </AnimatePresence>

      <motion.div
        animate={{
          x: pos.x,
          y: pos.y,
        }}
        onUpdate={(latest) => {
          currentX.current = latest.x;
          currentY.current = latest.y;

          // High-Performance central collision detection
          const now = Date.now();
          if (now - (bugRef.current?.lastDispatch || 0) > 32) {
            if (bugRef.current && collisionRegistry?.current) {
              bugRef.current.lastDispatch = now;
              const bugRect = bugRef.current.getBoundingClientRect();
              const bugCenterX = bugRect.left + bugRect.width / 2;
              const bugCenterY = bugRect.top + bugRect.height / 2;

              let hitId = null;
              // Check against registry in memory (O(N) but No DOM reads!)
              for (const [id, entry] of collisionRegistry.current.entries()) {
                const { rect } = entry;
                if (bugCenterX >= rect.left && bugCenterX <= rect.right &&
                  bugCenterY >= rect.top && bugCenterY <= rect.bottom) {
                  hitId = id;
                  break;
                }
              }

              window.dispatchEvent(new CustomEvent('bug-collision', {
                detail: { hitId, x: bugCenterX, y: bugCenterY }
              }));
            }
          }

          // Reactively update bug color based on actual position
          if (actuallyHovered || isReturning) {
            setBugColor(getColumnColor(latest.x));
          }
        }}
        onAnimationComplete={() => {
          setIsActuallyWalking(false);
          if (!actuallyHovered) {
            setIsReturning(false);
            setBugColor('#f43f5e');
          }
        }}
        transition={{
          x: { duration: isReturning ? returnDuration : (actuallyHovered ? 2.5 : 1.5), ease: isReturning ? "easeInOut" : "linear" },
          y: { duration: isReturning ? returnDuration : (actuallyHovered ? 2.5 : 1.5), ease: isReturning ? "easeInOut" : "linear" },
        }}
        className="z-[100] relative"
        ref={bugRef}
        style={{ color: bugColor, translateX: '-50%', translateY: '-50%' }}
      >
        <motion.div
          animate={{ rotate: rotation }}
          transition={{ duration: 0.4 }}
        >
          <motion.div
            animate={(actuallyHovered || isReturning) ? {
              rotate: actuallyHovered ? [0, -10, 10, -10, 0] : 0,
              x: actuallyHovered ? [0, -2, 2, -2, 0] : 0,
              scale: 1.5,
              filter: [`drop-shadow(0 0 2px ${bugColor})`, `drop-shadow(0 0 8px ${bugColor})`, `drop-shadow(0 0 2px ${bugColor})`]
            } : (isActuallyWalking ? {
              scale: 1.2,
              filter: `drop-shadow(0 0 4px ${bugColor}80)`
            } : { scale: 1, filter: 'none' })}
            transition={(isHovered || isReturning) ? {
              rotate: { duration: 0.3, repeat: Infinity },
              x: { duration: 0.3, repeat: Infinity },
              filter: { duration: 2, repeat: Infinity },
              scale: { duration: 0.4 } // Smooth scale transition
            } : { duration: 0.5 }}
          >
            <WalkingBugBody size={16} color={bugColor} isMoving={isActuallyWalking} />
          </motion.div>
        </motion.div>

        {/* Speech Bubble - Now stable outside bug's rotation/wiggle */}
        <AnimatePresence mode="wait">
          {showBubble && message && (
            <motion.div
              initial={{ opacity: 0, scale: 0.5, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: -95 }}
              exit={{ opacity: 0, scale: 0.5, y: 10 }}
              transition={{ type: "spring", stiffness: 400, damping: 25 }}
              className="absolute left-1/2 -translate-x-1/2 pointer-events-none z-[1001]"
            >
              <div className="bg-slate-950 border-2 border-white/20 text-white text-[11px] font-black px-4 py-2.5 rounded-2xl shadow-[0_20px_40px_rgba(0,0,0,0.8)] relative flex items-center justify-center min-w-[140px] backdrop-blur-md">
                <span className="text-center tracking-tight leading-tight">{message}</span>
                {/* Bubble Tail */}
                <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-slate-950 border-r-2 border-b-2 border-white/20 rotate-45" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

const getStatusHex = (status) => {
  switch (status) {
    case 'todo': 
    case 'pending':
      return '#f59e0b';
    case 'review': 
    case 'ready_for_review':
      return '#a855f7';
    case 'done':
    case 'completed':
    case 'resolved':
      return '#10b981';
    case 'bug_fixing': return '#f43f5e';
    case 'applied': return '#6366f1';
    default: return '#64748b';
  }
};

const getPriorityHex = (priority) => {
  switch (priority) {
    case 'urgent': return '#ef4444';
    case 'high': return '#f59e0b';
    case 'medium': return '#6366f1';
    case 'low': return '#10b981';
    default: return '#64748b';
  }
};

const getStatusStyles = (status) => {
  switch (status) {
    case 'todo':
      return 'bg-amber-500/5 border-amber-500/10 hover:border-amber-500/30';
    case 'review':
      return 'bg-purple-500/5 border-purple-500/10 hover:border-purple-500/30';
    case 'done':
      return 'bg-emerald-500/5 border-emerald-500/10 hover:border-emerald-500/30';
    case 'bug_fixing':
      return 'bg-rose-500/5 border-rose-500/10 hover:border-rose-500/30';
    case 'applied':
      return 'bg-indigo-500/5 border-indigo-500/10 hover:border-indigo-500/30';
    case 'backlog':
    default:
      return 'bg-slate-500/5 border-slate-500/10 hover:border-slate-500/30';
  }
};

const springTransition = {
  type: "spring",
  stiffness: 200,
  damping: 28,
  mass: 0.8
};

const BugStatCard = ({ sprint, t, activeProject, isActive, onToggle, collisionRegistry, isUiMode = false }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onToggle}
      className={`flex items-center gap-3 px-4 py-2 rounded-2xl border shadow-lg cursor-pointer relative transition-all select-none group/bugstat ${isActive
          ? (isUiMode ? 'bg-indigo-500/25 border-indigo-500/60 shadow-indigo-500/20 ring-1 ring-indigo-500/40' : 'bg-rose-500/25 border-rose-500/60 shadow-rose-500/20 ring-1 ring-rose-500/40')
          : (isUiMode ? 'bg-indigo-500/10 border-indigo-500/20 shadow-indigo-500/5 hover:bg-indigo-500/15 hover:border-indigo-500/40' : 'bg-rose-500/10 border-rose-500/20 shadow-rose-500/5 hover:bg-rose-500/15 hover:border-rose-500/40')
        }`}
    >
      {/* Active pulse indicator */}
      {isActive && (
        <motion.div
          className={`absolute inset-0 rounded-2xl border-2 pointer-events-none ${isUiMode ? 'border-indigo-500/40' : 'border-rose-500/40'}`}
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}
      <div className="flex items-center gap-2">
        <div className="w-[14px] h-[14px]">
          {isUiMode ? (
            <Sparkles size={14} className={isActive ? 'text-indigo-400' : 'text-indigo-500/60'} />
          ) : (
            <WalkingBug isHovered={isHovered || isActive} activeProject={activeProject} collisionRegistry={collisionRegistry} />
          )}
        </div>
        <div className="flex flex-col">
          <span className={`text-[8px] font-black uppercase tracking-widest leading-none mb-0.5 transition-colors ${isActive 
              ? (isUiMode ? 'text-indigo-400' : 'text-rose-400') 
              : (isUiMode ? 'text-indigo-500/60' : 'text-rose-500/60')
            }`}>
            {isUiMode ? (t('uiImprovements') || 'Mejoras UI') : t('resolvedBugs')}
          </span>
          <div className="flex items-center gap-1.5">
            <span className={`text-xs font-black ${isUiMode ? 'text-indigo-200' : 'text-rose-200'}`}>
              {sprint.resolvedCount || sprint.resolvedBugCount || 0}
              <span className={`text-[10px] mx-0.5 font-normal ${isUiMode ? 'text-indigo-500/50' : 'text-rose-500/50'}`}>/</span>
              <span className={`text-[10px] ${isUiMode ? 'text-indigo-400/70' : 'text-rose-400/70'}`}>{sprint.totalCount || sprint.totalBugCount || 0}</span>
            </span>
            <div className="w-1 h-1 rounded-full bg-rose-500/30" />
            <div className="flex items-center gap-0.5">
              <Zap size={10} className="text-amber-400" fill="currentColor" />
              <span className="text-xs font-black text-amber-200">{sprint.resolvedBugPoints || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const useScrollVelocity = (ref) => {
  const [isScrollingFast, setIsScrollingFast] = useState(false);
  const isFastRef = useRef(false);
  const lastScrollTop = useRef(0);
  const lastTime = useRef(Date.now());
  const timeoutRef = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const handleScroll = () => {
      const now = Date.now();
      const scrollTop = el.scrollTop;
      const dt = now - lastTime.current;
      
      // Calculate velocity
      if (dt > 15) {
        const distance = Math.abs(scrollTop - lastScrollTop.current);
        const velocity = distance / dt;
        
        // Threshold: 0.4px/ms (More aggressive Lite Mode)
        const isCurrentlyFast = velocity > 0.4;
        
        if (isCurrentlyFast !== isFastRef.current) {
          isFastRef.current = isCurrentlyFast;
          setIsScrollingFast(isCurrentlyFast);
        }

        lastScrollTop.current = scrollTop;
        lastTime.current = now;

        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          if (isFastRef.current) {
            isFastRef.current = false;
            setIsScrollingFast(false);
          }
        }, 150);
      }
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', handleScroll);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [ref]);

  return isScrollingFast;
};

const TicketCard = React.memo(React.forwardRef(({ ticket, onClick, registerTicket, isScrollingFast }, ref) => {
  const { lang, t } = useLanguage();

  const ps = useMemo(() => getPriorityStyles(ticket.priority || 'medium'), [ticket.priority]);
  const statusColor = useMemo(() => getStatusHex(ticket.boardColumn || ticket.status), [ticket.boardColumn, ticket.status]);

  const isReviewColumn = useMemo(() => {
    return ticket.boardColumn === 'review' || ticket.status === 'review' || ticket.status === 'ready_for_review';
  }, [ticket.boardColumn, ticket.status]);

  const [isShaking, setIsShaking] = useState(false);

  const handleClick = useCallback(() => {
    onClick(ticket);
  }, [onClick, ticket]);

  const isLocked = useMemo(() => {
    if (!ticket.lockedAt || ticket.status === 'completed' || ticket.status === 'done' || ticket.status === 'applied' || ticket.status === 'applied_local') return false;
    const lockTime = new Date(ticket.lockedAt).getTime();
    const now = new Date().getTime();
    return (now - lockTime) < 60 * 60 * 1000; // 1 hour
  }, [ticket.lockedAt, ticket.status]);

  const displayTitle = useMemo(() => {
    return ticket.message || ticket.description || ticket.title;
  }, [ticket.message, ticket.description, ticket.title]);

  const cardRef = useRef(null);
  const cachedRect = useRef(null);
  const isInView = useInView(cardRef, { margin: "200px 0px 200px 0px" });

  const updateRect = useCallback(() => {
    if (cardRef.current && isInView) {
      const rect = cardRef.current.getBoundingClientRect();
      const entry = {
        left: rect.left,
        right: rect.right,
        top: rect.top,
        bottom: rect.bottom
      };
      cachedRect.current = entry;
      if (registerTicket) {
        registerTicket(ticket._id, entry);
      }
    }
  }, [ticket._id, registerTicket, isInView]);

  useEffect(() => {
    if (!isInView) return;
    updateRect();

    return () => {
      if (registerTicket) {
        registerTicket(ticket._id, null);
      }
    };
  }, [updateRect, ticket._id, registerTicket, isInView]);

  useEffect(() => {
    if (!isInView) return;

    const handleBugCollision = (e) => {
      const { hitId } = e.detail;
      const isOver = hitId === ticket._id;

      if (isOver !== isShaking) {
        setIsShaking(isOver);
      }
    };

    window.addEventListener('bug-collision', handleBugCollision, { passive: true });

    return () => {
      window.removeEventListener('bug-collision', handleBugCollision);
    };
  }, [ticket._id, isShaking, isInView]);

  const animateProps = useMemo(() => {
    if (isScrollingFast) return { opacity: 1 };
    
    return {
      opacity: 1,
      scale: isShaking ? 1.01 : 1,
      ...(isLocked ? {
        boxShadow: [`0 0 20px ${statusColor}20`, `0 0 40px ${statusColor}50`, `0 0 20px ${statusColor}20`],
      } : isShaking ? {
        rotate: [0, -1.2, 1.2, -0.8, 0.8, 0],
        x: [0, -2, 2, -1, 1, 0],
        y: [0, -0.5, 0.8, -0.5, 0],
        boxShadow: `0 0 40px ${statusColor}40`,
        outline: `1.5px solid ${statusColor}80`,
      } : { rotate: 0, x: 0, y: 0 })
    };
  }, [isShaking, isLocked, statusColor, isScrollingFast]);

  const transitionProps = useMemo(() => {
    if (isScrollingFast) return { duration: 0 };
    
    return (
      isLocked ? {
        boxShadow: { duration: 2, repeat: Infinity, ease: "easeInOut" },
        duration: 0.3
      } : isShaking ? {
        duration: 0.15,
        repeat: Infinity,
        ease: "easeInOut"
      } : { duration: 0.3 }
    );
  }, [isLocked, isShaking, isScrollingFast]);

  const containerStyle = useMemo(() => ({
    backgroundColor: ps.bgSubtle,
    borderColor: ps.borderSubtle,
    '--status-col': statusColor
  }), [ps.bgSubtle, ps.borderSubtle, statusColor]);

  return (
    <motion.div
      ref={(node) => {
        cardRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={animateProps}
      transition={transitionProps}
      exit={{ opacity: 0, scale: 0.95 }}
      onClick={handleClick}
      className={`p-5 rounded-[24px] border transition-all cursor-pointer group backdrop-blur-sm relative min-h-[220px] ticket-card-base ${isScrollingFast ? 'ticket-card-lite' : ''}`}
      style={{
        ...containerStyle,
        contain: 'layout',
        willChange: 'transform, opacity'
      }}
    >
      <BorderBeam color={statusColor} borderRadius={24} isVisible={isInView && !isScrollingFast} duration={15} />

      {/* Applied by AI Badge for Pending/Bugs column identification */}
      {ticket.status === 'applied' && (ticket.boardColumn === 'todo' || ticket.boardColumn === 'bug_fixing' || !ticket.boardColumn) && (
        <div className="absolute -top-1.5 -right-1.5 bg-indigo-600 text-white rounded-full p-1.5 shadow-lg border-2 border-slate-900 z-10 animate-pulse">
          <Monitor size={12} fill="currentColor" className="opacity-90" />
        </div>
      )}

      {/* Correction Badge - Only if has at least 1 correction record AND is in the Review column */}
      {ticket.status === 'review' && (Array.isArray(ticket.corrections) ? ticket.corrections.length > 0 : !!ticket.corrections) && (
        <div className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full p-1.5 shadow-lg border-2 border-slate-900 z-10 animate-bounce">
          <MessageSquare size={12} fill="currentColor" className="opacity-90" />
        </div>
      )}

      <div className="flex justify-between items-start mb-4 gap-4">
        <div className="flex flex-col gap-2 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[9px] font-black uppercase tracking-tighter bg-white/5 px-2 py-0.5 rounded border border-white/5 transition-colors self-start" style={{ color: ps.textSubtle }}>
              {ticket.key || `VG-${ticket._id.substring(ticket._id.length - 3).toUpperCase()}`}
            </span>
            {ticket.workflowId && (
              <div className="flex items-center gap-1.5 px-1.5 py-0.5 rounded-md bg-indigo-500/10 text-indigo-400 text-[9px] font-black border border-indigo-500/20 shadow-sm shadow-indigo-500/5">
                <Waypoints size={9} />
                {ticket.steps?.length > 0 ? `${ticket.steps.length + 1} ${lang === 'es' ? 'PASOS' : 'STEPS'}` : 'WORKFLOW'}
              </div>
            )}
            {ticket.planning?.score && (
              <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-500/10 text-amber-500 text-[9px] font-black border border-amber-500/20 shadow-sm shadow-amber-500/5">
                <Zap size={9} fill="currentColor" />
                {ticket.planning.score}
              </div>
            )}
            {isLocked && !isReviewColumn && ticket.status !== 'applied' && ticket.status !== 'applied_local' && (
              <div className="flex items-center gap-1.5 px-1.5 py-0.5 rounded-md bg-emerald-500/10 text-emerald-500 text-[9px] font-black border border-emerald-500/20 shadow-sm shadow-emerald-500/5 transition-all">
                {ticket.resolver?.avatar ? (
                  <div className="w-3.5 h-3.5 rounded-full overflow-hidden border border-emerald-500/30 flex-none bg-emerald-500/20">
                    <img src={ticket.resolver.avatar} className="w-full h-full object-cover" alt="" />
                  </div>
                ) : (
                  <RefreshCw size={9} className="animate-spin" />
                )}
                {ticket.status === 'review'
                  ? (lang === 'es' ? 'PROCESADO' : 'PROCESSED')
                  : (lang === 'es' ? 'PROCESANDO' : 'PROCESSING')}
              </div>
            )}
            {ticket.status === 'applied' && (ticket.boardColumn === 'todo' || ticket.boardColumn === 'bug_fixing' || !ticket.boardColumn) && (
              <div className="flex items-center gap-1.5 px-1.5 py-0.5 rounded-md bg-indigo-500/10 text-indigo-400 text-[9px] font-black border border-indigo-500/20 shadow-sm shadow-indigo-500/5">
                {ticket.resolver?.avatar ? (
                  <div className="w-3.5 h-3.5 rounded-full overflow-hidden border border-indigo-500/30 flex-none bg-indigo-500/20">
                    <img src={ticket.resolver.avatar} className="w-full h-full object-cover" alt="" />
                  </div>
                ) : (
                  <Monitor size={9} fill="currentColor" />
                )}
                {t('appliedLocally')}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest border self-start" style={{ backgroundColor: ps.bg, color: ps.text, borderColor: ps.border }}>
              {t(ticket.priority || 'medium')}
            </span>
            {ticket.planning?.tipo && (
              <span className="px-2 py-0.5 rounded-md text-[8px] font-black bg-white/5 border border-white/5 text-slate-400 uppercase tracking-tighter">
                {t(ticket.planning.tipo.toLowerCase().replace(/-(.)/g, (m, c) => c.toUpperCase()).replace(/^ai/, 'ai'))}
              </span>
            )}
          </div>
        </div>
        <div
          className="p-1.5 rounded-full"
          style={{ backgroundColor: ps.bgSubtle, color: ps.text }}
        >
          {ticket.status === 'bug_fixing' || ticket.type === 'bug' || ticket.originalStatus === 'bug_fixing' ? (
            <Bug size={14} className={ticket.priority === 'urgent' ? 'animate-pulse' : ''} />
          ) : (
            ticket.priority === 'urgent' ? <Zap size={14} className="animate-pulse" /> : <AlertCircle size={14} />
          )}
        </div>
      </div>

      {ticket.focusAreas && ticket.focusAreas.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {ticket.focusAreas.includes('backend') && (
            <span className="px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20 text-[9px] font-black uppercase tracking-tighter flex items-center gap-1">
              <Globe size={9} /> Backend
            </span>
          )}
          {ticket.focusAreas.includes('frontend') && (
            <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[9px] font-black uppercase tracking-tighter flex items-center gap-1">
              <Layers size={9} /> Frontend
            </span>
          )}
          {ticket.focusAreas.includes('ux') && (
            <span className="px-2 py-0.5 rounded bg-fuchsia-500/10 text-fuchsia-400 border border-fuchsia-500/20 text-[9px] font-black uppercase tracking-tighter flex items-center gap-1">
              <LinkIcon size={9} /> UX/UI
            </span>
          )}
          {ticket.steps && ticket.steps.length > 0 && (
            <span className="px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 text-[9px] font-black uppercase tracking-tighter flex items-center gap-1 shadow-sm shadow-indigo-500/10">
              <Waypoints size={9} /> {ticket.steps.length + 1} {t('steps') || 'Pasos'}
            </span>
          )}
        </div>
      )}
      <h4
        className="text-[13px] font-semibold text-slate-200 line-clamp-3 mb-4 leading-relaxed group-hover:text-white transition-colors"
      >
        {displayTitle}
      </h4>

      <div
        className="flex items-center justify-between pt-4 border-t border-white/5"
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary/30 to-purple-500/30 border border-white/10 flex items-center justify-center text-[9px] text-white font-black overflow-hidden shadow-lg shadow-primary/10">
              {isInView && ticket.creator?.avatar ? (
                <img
                  src={ticket.creator.avatar}
                  className="w-full h-full object-cover"
                  style={{ aspectRatio: '1/1' }}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                ticket.creator?.name?.[0] || 'A'
              )}
            </div>
            <div className="flex flex-col">
              <span className="text-[7px] font-black text-primary/70 uppercase leading-none mb-0.5">{t('creatorLabel') || 'Creador'}</span>
              <span className="text-[9px] text-slate-200 font-bold leading-none">{ticket.creator?.name?.split(' ')[0] || t('admin')}</span>
            </div>
          </div>

          {ticket.resolver && (
            <div className="flex items-center gap-2 ml-1 border-l border-white/10 pl-3">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-500/30 to-teal-500/30 border border-white/10 flex items-center justify-center text-[9px] text-white font-black overflow-hidden shadow-lg shadow-emerald-500/10">
                {isInView && ticket.resolver.avatar ? (
                  <img
                    src={ticket.resolver.avatar}
                    className="w-full h-full object-cover"
                    style={{ aspectRatio: '1/1' }}
                    alt=""
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  ticket.resolver.name?.[0] || 'R'
                )}
              </div>
              <div className="flex flex-col">
                <span className="text-[7px] font-black text-emerald-500 uppercase leading-none mb-0.5">{getResolverStatusLabel(ticket, t)}</span>
                <span className="text-[9px] text-slate-300 font-bold leading-none">{ticket.resolver.name?.split(' ')[0]}</span>
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-[9px] text-slate-500 font-bold uppercase tracking-wider">
          <Clock size={10} className="text-primary/50" />
          {new Date(ticket.createdAt).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </motion.div>
  );
}));

const TicketSkeleton = () => (
  <div className="p-5 rounded-[24px] border border-white/5 bg-white/[0.02] backdrop-blur-sm relative min-h-[220px] h-[220px] flex flex-col">
    <div className="flex justify-between items-start mb-4 gap-4">
      <div className="flex flex-col gap-2 flex-1">
        <div className="w-16 h-4 rounded bg-white/5 skeleton-shimmer" />
        <div className="w-24 h-4 rounded bg-white/5 skeleton-shimmer" />
      </div>
      <div className="w-8 h-8 rounded-full bg-white/5 skeleton-shimmer" />
    </div>
    {/* LCP Candidate Shadow: Exact same font metrics as real ticket titles */}
    <h4 className="text-[13px] font-semibold text-white/5 leading-relaxed skeleton-shimmer mb-4 line-clamp-3">

    </h4>
    <div className="mt-auto pt-4 border-t border-white/5 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-full bg-white/5 skeleton-shimmer" />
        <div className="w-16 h-3 rounded bg-white/5 skeleton-shimmer" />
      </div>
      <div className="w-20 h-3 rounded bg-white/5 skeleton-shimmer" />
    </div>
  </div>
);

const ColumnSkeleton = ({ id }) => {
  const { t } = useLanguage();
  const statusColor = getStatusHex(id);

  return (
    <div className="flex-1 min-w-0 h-full flex flex-col relative bg-transparent">
      {/* Header Skeleton */}
      <div className="px-8 py-10 flex items-center justify-between relative flex-none">
        <div className="flex items-center gap-4">
          <div className="w-2.5 h-2.5 rounded-full bg-white/10" style={{ backgroundColor: `${statusColor}40` }} />
          <div className="w-24 h-4 rounded bg-white/5 skeleton-shimmer" />
        </div>
        <div className="w-8 h-6 rounded-xl bg-white/5 skeleton-shimmer" />
        <div className="absolute bottom-0 left-8 right-8 h-px bg-white/5" />
      </div>

      {/* Content Skeleton */}
      <div className="flex-1 px-8 pb-8 overflow-y-hidden space-y-4">
        {[1, 2, 3].map(i => <TicketSkeleton key={i} />)}
      </div>
    </div>
  );
};

const HeaderComponentSkeleton = ({ width, height = 44 }) => (
  <div
    className="rounded-xl bg-white/[0.03] border border-white/5 skeleton-shimmer"
    style={{ width, height }}
  />
);


const Column = React.memo(({ id, tickets, onOpenTicket, onLoadMore, hasMore, isLoadingMore, totalCount, registerTicket, isStatsLoading = false }) => {
  const { t } = useLanguage();
  const statusStyles = getStatusStyles(id);
  const statusColor = getStatusHex(id);
  const parentRef = React.useRef(null);
  const isScrollingFast = useScrollVelocity(parentRef);

  const estimateSize = useCallback(() => 230, []);
  const getScrollElement = useCallback(() => parentRef.current, []);

  const virtualizer = useVirtualizer({
    count: tickets.length,
    getScrollElement,
    estimateSize,
    overscan: 6,
    paddingEnd: 250,
  });

  const virtualItems = virtualizer.getVirtualItems();

  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const scrollPercent = (scrollTop + clientHeight) / scrollHeight;

    // Facebook-style pre-filling: load next page at 60% scroll
    if (scrollPercent > 0.6 && hasMore && !isLoadingMore) {
      onLoadMore(id);
    }
  }, [hasMore, isLoadingMore, onLoadMore, id]);

  const headerAnimateProps = useMemo(() => ({
    opacity: [0.4, 1, 0.4],
    scale: [0.95, 1.05, 0.95],
    boxShadow: [
      `0 0 8px ${statusColor}40`,
      `0 0 16px ${statusColor}`,
      `0 0 8px ${statusColor}40`
    ]
  }), [statusColor]);

  const headerTransitionProps = useMemo(() => ({
    duration: 2,
    repeat: Infinity,
    ease: "easeInOut"
  }), []);

  const headerIndicatorStyle = useMemo(() => ({
    background: `linear-gradient(to right, ${statusColor}, transparent)`
  }), [statusColor]);

  return (
    <div
      className="flex-1 min-w-0 h-full flex flex-col transition-all duration-300 relative group/col bg-transparent"
      style={{ contain: 'layout' }}
    >
      {/* Status-Colored Vertical Divider (Right Side) */}
      <div
        className="absolute top-0 bottom-0 right-0 w-px opacity-[0.04] group-hover/col:opacity-15 transition-opacity duration-700 pointer-events-none last:hidden"
        style={{ backgroundColor: statusColor }}
      />

      {/* Column Header - Integrated */}
      <div className="px-8 py-10 flex items-center justify-between pointer-events-none relative flex-none">
        <div className="flex items-center gap-4">
          <motion.div
            animate={headerAnimateProps}
            transition={headerTransitionProps}
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: statusColor }}
          />
          <h3 className="text-[12px] font-black uppercase tracking-[0.35em] text-white/80 font-mono italic">
            {t(id)}
          </h3>
        </div>
        <span className="px-3.5 py-1.5 rounded-xl bg-white/5 border border-white/10 text-[13px] font-black text-slate-400 font-mono group-hover/col:text-white transition-all shadow-lg min-w-[32px] text-center">
          {isStatsLoading ? (
            <motion.div animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.5, repeat: Infinity }}>...</motion.div>
          ) : (totalCount !== undefined ? totalCount : tickets.length)}
        </span>

        {/* Dynamic Indicator Line */}
        <div
          className="absolute bottom-0 left-8 right-8 h-px opacity-20"
          style={headerIndicatorStyle}
        />
      </div>

      {/* Column Content - Max Space with Virtualization */}
      <div
        ref={parentRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto custom-scrollbar px-6 relative"
      >
        <div
          style={{
            height: `${virtualizer.getTotalSize() + 48}px`, // 48px top offset (pt-12), paddingEnd handled by virtualizer
            width: '100%',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${(virtualItems[0]?.start ?? 0) + 48}px)`,
              display: 'flex',
              flexDirection: 'column',
              gap: '24px'
            }}
          >
            {virtualItems.map((virtualItem) => {
              const ticket = tickets[virtualItem.index];
              if (!ticket) return null;
              return (
                <div
                  key={virtualItem.key}
                  ref={virtualizer.measureElement}
                  data-index={virtualItem.index}
                >
                  <TicketCard
                    ticket={ticket}
                    onClick={onOpenTicket}
                    registerTicket={registerTicket}
                    isScrollingFast={isScrollingFast}
                  />
                </div>
              );
            })}
          </div>

          {isLoadingMore && (
            <div
              style={{
                position: 'absolute',
                bottom: '24px',
                left: 0,
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                padding: '20px'
              }}
            >
              <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
            </div>
          )}
        </div>
      </div>

      {/* Column Subtle Background Glow */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-white/[0.01] to-transparent opacity-0 group-hover/col:opacity-100 transition-opacity duration-700" />
    </div>
  );
});

const StatusConfirmationModal = ({
  isOpen,
  targetStatus,
  onConfirm,
  onCancel,
  isUpdating,
  corrections,
  setCorrections,
  isReverting,
  isResolvingCorrection,
  fixDescription,
  setFixDescription
}) => {
  const { t } = useLanguage();

  const statusColor = getStatusHex(targetStatus);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-md"
          onClick={onCancel}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative w-full max-w-md bg-[#0a0f18]/90 border rounded-[32px] overflow-hidden shadow-2xl p-8 backdrop-blur-2xl"
            style={{ borderColor: `${statusColor}40` }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="absolute top-0 right-0 w-32 h-32 blur-[60px] rounded-full translate-x-10 -translate-y-10 opacity-20"
              style={{ backgroundColor: statusColor }}
            />

            <div className="relative z-10 space-y-6 text-center">
              <div className="flex flex-col items-center gap-4 mb-4">
                <div
                  className="w-16 h-16 rounded-3xl flex items-center justify-center border shadow-xl mb-2"
                  style={{ backgroundColor: `${statusColor}15`, borderColor: `${statusColor}30`, color: statusColor }}
                >
                  {targetStatus === 'done' ? <CheckCircle2 size={32} strokeWidth={3} /> :
                    targetStatus === 'review' ? <Layers size={32} strokeWidth={3} /> :
                      targetStatus === 'bug_fixing' ? <Bug size={32} strokeWidth={3} /> :
                        targetStatus === 'applied' ? <Bot size={32} strokeWidth={3} /> :
                          <Clock size={32} strokeWidth={3} />}
                </div>
                <div>
                  <h3 className="text-2xl font-display font-black text-white leading-tight">
                    {isReverting ? (t('correctionsRequired') || 'Correcciones Necesarias') : t('confirmStatusTitle')}
                  </h3>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 font-mono italic mt-1">
                    {t(targetStatus)}
                  </p>
                </div>
              </div>

              <p className="text-slate-400 text-sm leading-relaxed font-medium">
                {isReverting
                  ? (t('correctionsDesc') || 'Por favor, detalla las correcciones necesarias para volver a este estado.')
                  : t('confirmStatusMessage', { status: t(targetStatus).toLowerCase() })}
              </p>

              {isReverting && (
                <div className="space-y-3">
                  <textarea
                    className="w-full h-32 bg-white/5 border border-white/10 rounded-2xl p-4 text-white text-sm focus:outline-none focus:border-primary/50 transition-all custom-scrollbar placeholder:text-slate-600 font-medium"
                    placeholder={t('correctionsPlaceholder') || 'Escribe aquí tu feedback...'}
                    value={corrections}
                    onChange={(e) => setCorrections(e.target.value)}
                    autoFocus
                  />
                  {!corrections.trim() && (
                    <p className="text-[10px] text-rose-500 font-black uppercase tracking-widest text-left pl-2">
                      {t('correctionsMandatory') || 'El contenido es obligatorio para retroceder'}
                    </p>
                  )}
                </div>
              )}

              {targetStatus === 'done' && (
                <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 space-y-2 text-left">
                  <div className="flex items-center gap-2 text-amber-500">
                    <AlertCircle size={16} />
                    <span className="text-[10px] font-black uppercase tracking-wider">{t('importantNote')}</span>
                  </div>
                  <p className="text-[11px] text-amber-200/70 font-medium leading-relaxed">
                    {t('evidenceBypassWarning')}
                  </p>
                </div>
              )}

              {targetStatus === 'review' && (
                <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 space-y-2 text-left">
                  <div className="flex items-center gap-2 text-amber-500">
                    <AlertCircle size={16} />
                    <span className="text-[10px] font-black uppercase tracking-wider">{t('importantNote')}</span>
                  </div>
                  <p className="text-[11px] text-amber-200/70 font-medium leading-relaxed">
                    {t('iaCommentWarning')}
                  </p>
                </div>
              )}

              {isResolvingCorrection && (
                <div className="space-y-3 text-left">
                  <div className="flex items-center gap-2 ml-1">
                    <div className="w-1 h-3 bg-emerald-500/40 rounded-full" />
                    <h4 className="text-[10px] font-black uppercase tracking-widest text-emerald-500/80">{t('fixDescriptionLabel')}</h4>
                  </div>
                  <textarea
                    className="w-full h-24 bg-white/5 border border-white/10 rounded-2xl p-4 text-white text-sm focus:outline-none focus:border-emerald-500/50 transition-all custom-scrollbar placeholder:text-slate-600 font-medium"
                    placeholder={t('fixDescriptionPlaceholder') || 'Describe los cambios realizados...'}
                    value={fixDescription}
                    onChange={(e) => setFixDescription(e.target.value)}
                    autoFocus
                  />
                </div>
              )}

              <div className="flex flex-col gap-3 pt-2">
                <button
                  onClick={onConfirm}
                  disabled={isUpdating || (isReverting && !corrections.trim())}
                  className="w-full py-4 rounded-2xl text-white text-[11px] font-black uppercase tracking-widest transition-all hover:brightness-110 active:scale-95 shadow-xl shadow-black/20 flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: statusColor,
                    opacity: (isUpdating || (isReverting && !corrections.trim())) ? 0.5 : 1,
                    cursor: (isUpdating || (isReverting && !corrections.trim())) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isUpdating ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : null}
                  {isReverting ? (t('sendCorrections') || 'Enviar y Retroceder') : t('confirm')}
                </button>
                <button
                  onClick={onCancel}
                  className="w-full py-4 rounded-2xl bg-white/5 text-slate-500 text-[11px] font-black uppercase tracking-widest transition-all hover:bg-white/10 border border-white/5 active:scale-95"
                >
                  {t('cancel')}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

const ConfirmActionModal = ({ isOpen, onClose, onConfirm, title, message, type = 'info' }) => {
  const { lang, t } = useLanguage();
  const color = type === 'danger' ? '#f43f5e' : '#6366f1';

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[11000] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-md"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="relative w-full max-w-sm bg-[#0a0f18]/95 border rounded-[32px] overflow-hidden shadow-2xl p-8 backdrop-blur-2xl"
            style={{ borderColor: `${color}40` }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="absolute top-0 right-0 w-32 h-32 blur-[60px] rounded-full translate-x-10 -translate-y-10 opacity-20"
              style={{ backgroundColor: color }}
            />

            <div className="relative z-10 space-y-6 text-center">
              <div className="flex flex-col items-center gap-4">
                <div
                  className="w-16 h-16 rounded-3xl flex items-center justify-center border shadow-xl"
                  style={{ backgroundColor: `${color}15`, borderColor: `${color}30`, color: color }}
                >
                  {type === 'danger' ? <Trash2 size={32} strokeWidth={2.5} /> : <AlertCircle size={32} strokeWidth={2.5} />}
                </div>
                <div>
                  <h3 className="text-xl font-display font-black text-white leading-tight">
                    {title}
                  </h3>
                  <p className="text-slate-400 text-sm mt-3 font-medium leading-relaxed">
                    {message}
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-3 pt-2">
                <button
                  onClick={() => {
                    onConfirm();
                    onClose();
                  }}
                  className="w-full py-4 rounded-2xl text-white text-[11px] font-black uppercase tracking-widest transition-all hover:brightness-110 active:scale-95 shadow-xl shadow-black/20"
                  style={{ backgroundColor: color }}
                >
                  {t('confirm') || 'Confirmar'}
                </button>
                <button
                  onClick={onClose}
                  className="w-full py-4 rounded-2xl bg-white/5 text-slate-500 text-[11px] font-black uppercase tracking-widest transition-all hover:bg-white/10 border border-white/5 active:scale-95"
                >
                  {t('cancel') || 'Cancelar'}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

const TicketModal = React.memo(({ ticket, onClose, onUpdate, mutateTickets, evidenceCache, setEvidenceCache, onOpenEvidence }) => {
  const { user } = useAuth();
  const { lang, t } = useLanguage();
  const { addToast } = useToast();
  const ps = getPriorityStyles(ticket.priority || 'medium');
  const scrollRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedDescription, setEditedDescription] = useState(ticket.description || '');
  const [editedPriority, setEditedPriority] = useState(ticket.priority || 'medium');
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingPriority, setIsSavingPriority] = useState(false);

  const [isLoadingScreenshot, setIsLoadingScreenshot] = useState(false);
  const [showScrollArrow, setShowScrollArrow] = useState(false);
  const [correctionsText, setCorrectionsText] = useState("");
  const [isConfirmingStatus, setIsConfirmingStatus] = useState(false);
  const [targetStatus, setTargetStatus] = useState(null);
  const [isAfterScreenshotLoaded, setIsAfterScreenshotLoaded] = useState(false);
  const [isUploadingEvidence, setIsUploadingEvidence] = useState(false);
  const [fixDescription, setFixDescription] = useState("");
  const [editingCorrectionId, setEditingCorrectionId] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editingCorrectionText, setEditingCorrectionText] = useState("");
  const [confirmModal, setConfirmModal] = useState(null); // { title, message, onConfirm, type }
  const activeCorrection = Array.isArray(ticket.corrections) ? ticket.corrections.find(c => c.status === 'pending') : null;
  const isResolvingCorrection = (targetStatus === 'review' || targetStatus === 'done') && !!activeCorrection;
  const isRevertingStatus = (targetStatus === 'todo' || targetStatus === 'bug_fixing') && (ticket.status === 'review' || ticket.status === 'ready_for_review');
  const isReviewStatus = ticket.status === 'review' || ticket.status === 'done' || ticket.status === 'completed' || ticket.status === 'ready_for_review';

  const handleStatusChange = async () => {
    const originalTarget = targetStatus;
    if (!originalTarget) return; // Guard against null

    const mapping = {
      'todo': 'pending',
      'review': 'ready_for_review',
      'done': 'completed',
      'bug_fixing': 'bug_fixing'
    };
    const apiStatus = mapping[originalTarget] || originalTarget;

    // Optimistic UI update - Use the internal column ID
    let updatedTicket = { ...ticket, status: originalTarget };

    // Handle Corrections Array
    let newCorrections = Array.isArray(ticket.corrections) ? [...ticket.corrections] : [];
    if (!Array.isArray(ticket.corrections) && ticket.corrections) {
      // Legacy string migration (Internal optimistic only)
      newCorrections = [{ id: 'legacy', text: ticket.corrections, status: 'resolved', createdAt: ticket.updatedAt }];
    }

    // 1. If reverting to bug_fixing or todo, add a NEW correction record
    if (isRevertingStatus && correctionsText.trim()) {
      newCorrections.push({
        id: crypto.randomUUID?.() || Math.random().toString(36).substring(7),
        text: correctionsText.trim(),
        createdAt: new Date().toISOString(),
        status: 'pending',
        author: {
          name: user?.name,
          avatar: user?.avatar
        }
      });
    }

    // 2. If resolving a ticket that has an active correction, mark it resolved
    if (isResolvingCorrection) {
      newCorrections = newCorrections.map(c =>
        c.status === 'pending'
          ? { ...c, status: 'resolved', resolvedAt: new Date().toISOString(), fixDescription: fixDescription.trim() }
          : c
      );
    }

    updatedTicket.corrections = newCorrections;

    // Set resolver data
    if (['review', 'ready_for_review', 'done', 'completed'].includes(apiStatus) && user) {
      updatedTicket.resolver = { name: user.name, email: user.email, avatar: user.avatar };
    }

    // Optimistic update via onUpdate
    setIsConfirmingStatus(false);
    setTargetStatus(null);
    if (onUpdate) onUpdate(updatedTicket);

    // Cleanup local state
    const currentCorrections = correctionsText;
    const currentFix = fixDescription;
    setCorrectionsText("");
    setFixDescription("");
    onClose();

    try {
      const payload = {
        status: apiStatus,
        corrections: newCorrections
      };

      if (isRevertingStatus && currentCorrections.trim()) {
        payload.metadata = {
          ...ticket.metadata,
          lastCorrectionBy: user?.name,
          correctionDate: new Date().toISOString()
        };
      }

      if (apiStatus === 'completed' && user) {
        payload.resolver = { name: user.name, email: user.email, avatar: user.avatar };
      }

      await api.patch(`/tickets/${ticket._id}`, payload);
    } catch (err) {
      // Failed to update ticket status
    }
  };

  const handleDeleteCorrection = (corrId) => {
    const corr = (ticket.corrections || []).find(c => (c.id === corrId || c._id === corrId));
    const snippet = corr?.text ? `"${corr.text.substring(0, 50)}${corr.text.length > 50 ? '...' : ''}"` : '';

    setConfirmModal({
      title: t('deleteCorrectionTitle'),
      message: `${t('confirmDeleteCorrection')}${snippet ? `\n\n${snippet}` : ''}`,
      type: 'danger',
      onConfirm: async () => {
        const updatedCorrections = (ticket.corrections || []).filter(c => c.id !== corrId && c._id !== corrId);
        
        // Optimistic UI update
        if (onUpdate) onUpdate({ ...ticket, corrections: updatedCorrections });
        
        try {
          await api.patch(`/tickets/${ticket._id}`, { corrections: updatedCorrections });
          addToast(t('correctionDeleted') || 'Corrección eliminada', 'success');
        } catch (err) {
          // Failed to delete correction
          addToast(t('errorDeletingCorrection') || 'Error al eliminar corrección', 'error');
        }
      }
    });
  };

  const handleDeleteTicket = () => {
    setConfirmModal({
      title: t('confirmDeleteTicket') || 'Delete Ticket',
      message: t('confirmDeleteTicketMsg', { id: ticket.key || ticket._id.substring(ticket._id.length - 8).toUpperCase() }),
      type: 'danger',
      onConfirm: async () => {
        try {
          await api.delete(`/tickets/${ticket._id}`);
          addToast(t('toastTicketDeleted') || 'Ticket deleted', 'success');
          if (mutateTickets) mutateTickets();
          onClose();
        } catch (err) {
          // Failed to delete ticket
          addToast(t('toastError') || 'Error deleting ticket', 'error');
        }
      }
    });
  };

  const handleSaveEditCorrection = async (corrId) => {
    if (!editingCorrectionText.trim()) return;
    
    const updatedCorrections = (ticket.corrections || []).map(c => 
      (c.id === corrId || c._id === corrId) ? { ...c, text: editingCorrectionText.trim() } : c
    );
    
    // Optimistic UI update
    if (onUpdate) onUpdate({ ...ticket, corrections: updatedCorrections });
    setEditingCorrectionId(null);
    setEditingCorrectionText("");

    try {
      await api.patch(`/tickets/${ticket._id}`, { corrections: updatedCorrections });
      addToast(t('correctionUpdated') || 'Correction updated', 'success');
    } catch (err) {
      // Failed to update correction
      addToast(t('errorUpdatingCorrection') || 'Error updating correction', 'error');
    }
  };

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      // Show arrow if there is at least 20px more content to scroll down
      setShowScrollArrow(scrollTop + clientHeight < scrollHeight - 20);
    }
  };

  useEffect(() => {
    // Wait for the mount animation to settle to avoid layout thrashing
    const timeoutId = setTimeout(checkScroll, 300);
    window.addEventListener('resize', checkScroll);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkScroll);
    };
  }, [ticket.description, ticket.hasScreenshot, ticket.planning, isEditing]);

  // Effect to auto-fetch original evidence if needed for comparison view
  useEffect(() => {
    // If we have resolved evidence but no original evidence in cache, fetch original
    if (ticket.hasResolvedScreenshot && !evidenceCache[ticket._id] && !isLoadingScreenshot) {
      handleFetchEvidence(null, false, 'original');
    }
    // If we have resolved evidence but it's not in cache/object, fetch resolved
    if (ticket.hasResolvedScreenshot && !ticket.resolvedScreenshot && !evidenceCache[`${ticket._id}_resolved`] && !isAfterScreenshotLoaded) {
      handleFetchEvidence(null, false, 'resolved');
    }
  }, [ticket.hasScreenshot, ticket.hasResolvedScreenshot, ticket.resolvedScreenshot, evidenceCache[ticket._id], evidenceCache[`${ticket._id}_resolved`]]);

  const handleFetchEvidence = async (specificUrl, openOverlay = true, type = 'original') => {
    if (specificUrl) {
      if (openOverlay) onOpenEvidence(specificUrl);
      return;
    }

    const cacheKey = type === 'original' ? ticket._id : `${ticket._id}_resolved`;

    if (!evidenceCache[cacheKey]) {
      if (type === 'original') setIsLoadingScreenshot(true);

      try {
        const endpoint = type === 'original' ? `/tickets/${ticket._id}/screenshot` : `/tickets/${ticket._id}/resolved-screenshot`;
        const res = await api.get(endpoint);
        const url = res.data[type === 'original' ? 'screenshot' : 'resolvedScreenshot'];

        setEvidenceCache(prev => ({ ...prev, [cacheKey]: url }));
        if (openOverlay) onOpenEvidence(url);
      } catch (err) {
        // Failed to fetch evidence
      } finally {
        if (type === 'original') setIsLoadingScreenshot(false);
      }
    } else {
      if (openOverlay) onOpenEvidence(evidenceCache[cacheKey]);
    }
  };

  const handleUploadEvidence = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Limit to 5MB
    if (file.size > 5 * 1024 * 1024) {
      addToast(lang === 'es' ? 'La imagen es demasiado grande (máx 5MB)' : 'Image is too large (max 5MB)', 'error');
      return;
    }

    setIsUploadingEvidence(true);
    const reader = new FileReader();

    reader.onloadend = async () => {
      const base64Data = reader.result;
      try {
        const response = await api.patch(`/tickets/${ticket._id}`, {
          resolvedScreenshot: base64Data
        });

        // Update local cache and state
        setEvidenceCache(prev => ({ ...prev, [`${ticket._id}_resolved`]: base64Data }));

        if (onUpdate) {
          onUpdate(response.data || { ...ticket, resolvedScreenshot: base64Data, hasResolvedScreenshot: true });
        }

        addToast(t('uploadSuccess'), 'success');
      } catch (err) {
        /* Failed to upload manual evidence handled silently */
        addToast(t('uploadError'), 'error');
      } finally {
        setIsUploadingEvidence(false);
      }
    };

    reader.onerror = () => {
      addToast(t('uploadError'), 'error');
      setIsUploadingEvidence(false);
    };

    reader.readAsDataURL(file);
  };

  if (!ticket) return null;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Backend now supports 'description' separately from 'message'
      const response = await api.patch(`/tickets/${ticket._id}`, {
        description: editedDescription,
        priority: editedPriority
      });

      const updatedData = response.data || { ...ticket, description: editedDescription, priority: editedPriority };

      if (onUpdate) {
        onUpdate(updatedData);
      }
      setIsEditing(false);
    } catch (err) {
      // Failed to update ticket description
    } finally {
      setIsSaving(false);
    }
  };

  const handlePriorityChange = async (newPriority) => {
    setEditedPriority(newPriority);
    setIsSavingPriority(true);
    try {
      const response = await api.patch(`/tickets/${ticket._id}`, {
        priority: newPriority
      });
      if (onUpdate) {
        onUpdate(response.data || { ...ticket, priority: newPriority });
      }
    } catch (err) {
      // Failed to update priority
    } finally {
      setIsSavingPriority(false);
    }
  };


  const handleDiscard = () => {
    setEditedDescription(ticket.description || '');
    setEditedPriority(ticket.priority || 'medium');
    setIsEditing(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5, ease: "easeInOut" }}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-auto overflow-hidden"
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/80 backdrop-blur-3xl"
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: "spring", stiffness: 450, damping: 35 }}
        role="dialog"
        className="relative w-full max-w-2xl border rounded-[24px] overflow-visible shadow-2xl flex flex-col max-h-[80vh] backdrop-blur-xl"
        style={{
          backgroundColor: ps.bgSubtle,
          borderColor: ps.borderSubtle,
          boxShadow: `0 10px 20px -5px rgba(0,0,0,0.5)`
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <BorderBeam color={getStatusHex(ticket.boardColumn || ticket.status)} borderRadius={24} />

        {/* Modal Header */}
        <div className="p-8 pb-4 flex justify-between items-start border-b border-white/5 relative bg-white/[0.01] flex-none">
          <div className="space-y-4 flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <span 
                className="text-[9px] font-black uppercase tracking-[0.2em] px-3 py-1 rounded-lg border transition-colors shadow-sm flex items-center gap-2"
                style={{ 
                  backgroundColor: `${getStatusHex(ticket.boardColumn || ticket.status)}15`, 
                  color: getStatusHex(ticket.boardColumn || ticket.status), 
                  borderColor: `${getStatusHex(ticket.boardColumn || ticket.status)}30` 
                }}
              >
                {ticket.resolver?.avatar && (ticket.status === 'bug_fixing' || ticket.status === 'in_progress' || ticket.status === 'applied') && (
                  <div className="w-4 h-4 rounded-full overflow-hidden border border-current opacity-80 bg-white/10 flex-none">
                    <img src={ticket.resolver.avatar} className="w-full h-full object-cover" alt="" />
                  </div>
                )}
                {t(ticket.status)}
              </span>
              <motion.button
                whileHover={{ scale: 1.05, backgroundColor: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.1)' }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  const key = ticket.key || `VG-${ticket._id.substring(ticket._id.length - 8).toUpperCase()}`;
                  navigator.clipboard.writeText(key);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className={`text-[10px] font-black tracking-widest font-mono px-3 py-1 rounded border flex items-center gap-2 transition-all cursor-pointer group ${
                  copied 
                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                    : 'bg-white/5 text-slate-500 border-white/5'
                }`}
                title={t('clickToCopy') || 'Click to copy ID'}
              >
                {copied ? (
                  <>
                    {t('copied') || '¡COPIADO!'}
                    <Check size={12} />
                  </>
                ) : (
                  <>
                    {ticket.key || `VG-${ticket._id.substring(ticket._id.length - 8).toUpperCase()}`}
                    <Copy size={12} className="text-slate-600 group-hover:text-slate-400 transition-colors" />
                  </>
                )}
              </motion.button>
            </div>
            <h2 className="text-2xl md:text-3xl font-display font-black text-white leading-[1.1] tracking-tight truncate max-w-full">
              {ticket.message || ticket.description || ticket.title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 rounded-xl bg-white/5 text-slate-400 hover:text-red-500 hover:bg-red-500/10 transition-all border border-white/10 flex-none ml-4"
          >
            <X size={20} />
          </button>
        </div>

        {/* Horizontal Metadata - Stitch Style */}
        <div className="px-8 py-3 bg-white/[0.02] border-b border-white/5 flex flex-wrap items-center gap-6 flex-none">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500/30 to-purple-500/30 border border-white/10 flex items-center justify-center text-[9px] text-white font-black overflow-hidden shadow-lg shadow-indigo-500/10">
              {ticket.creator?.avatar ? (
                <img src={ticket.creator.avatar} className="w-full h-full object-cover" alt="" />
              ) : (
                ticket.creator?.name?.[0] || 'A'
              )}
            </div>
            <div className="flex flex-col">
              <span className="text-[9px] font-black text-white uppercase tracking-[0.15em] leading-none mb-1">{t('owner')}</span>
              <span className="text-[10px] font-bold text-slate-200 leading-none">{ticket.creator?.name || t('admin')}</span>
            </div>
          </div>

          <div className="h-4 w-px bg-white/5" />

          {ticket.resolver && (
            <>
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500/30 to-teal-500/30 border border-white/10 flex items-center justify-center text-[9px] text-white font-black overflow-hidden shadow-lg shadow-emerald-500/10">
                  {ticket.resolver.avatar ? (
                    <img src={ticket.resolver.avatar} className="w-full h-full object-cover" alt="" />
                  ) : (
                    ticket.resolver.name?.[0] || 'R'
                  )}
                </div>
                <div className="flex flex-col">
                  <span className="text-[9px] font-black text-white uppercase tracking-[0.15em] leading-none mb-1">{getResolverStatusLabel(ticket, t)}</span>
                  <span className="text-[10px] font-bold text-slate-200 leading-none truncate max-w-[80px]">{ticket.resolver.name}</span>
                </div>
              </div>
              <div className="h-4 w-px bg-white/5" />
            </>
          )}

          <div className="flex flex-col">
            <span className="text-[9px] font-black text-white uppercase tracking-[0.15em] leading-none mb-1">{t('created')}</span>
            <span className="text-[10px] font-bold text-slate-200 leading-none">
              {new Date(ticket.createdAt).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>

          {ticket.resolvedAt && (
            <>
              <div className="h-4 w-px bg-white/5" />
              <div className="flex flex-col">
                <span className="text-[9px] font-black text-emerald-400 uppercase tracking-[0.15em] leading-none mb-1">{t('resolved')}</span>
                <span className="text-[10px] font-bold text-slate-200 leading-none">
                  {new Date(ticket.resolvedAt).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </>
          )}

          <div className="h-4 w-px bg-white/5" />

          <div className="flex flex-col">
            <span className="text-[9px] font-black text-white uppercase tracking-[0.15em] leading-none mb-1">{t('priority')}</span>
            <CustomDropdown
              className="w-32 -ml-3 scale-75 origin-left"
              isLoading={isSavingPriority}
              disabled={isReviewStatus}

              solid={true}
              backgroundColor={getPriorityStyles(editedPriority).text}
              options={[
                { value: 'urgent', label: t('urgent'), icon: Zap, activeColor: '#ef4444' },
                { value: 'high', label: t('high'), icon: AlertCircle, activeColor: '#f59e0b' },
                { value: 'medium', label: t('medium'), icon: Clock, activeColor: '#6366f1' },
                { value: 'low', label: t('low'), icon: CheckCircle2, activeColor: '#10b981' }
              ]}
              value={editedPriority}
              onChange={handlePriorityChange}
              placeholder={t('priority')}
              showHeader={false}
            />
          </div>

          <div className="h-4 w-px bg-white/5" />


        </div>

        {/* Modal Content - Editable Scroller Container */}
        <div className="flex-1 relative min-h-0 flex flex-col">
          <div
            ref={scrollRef}
            onScroll={checkScroll}
            className="flex-1 overflow-y-auto custom-scrollbar p-8 space-y-8 bg-[#0a0f18]/50"
          >

            {/* Editable Description Section */}
            <motion.div layout="position" className="space-y-4 max-w-full">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full" style={{ backgroundColor: ps.text }} />
                  <h3 className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-600 font-mono italic">{t('description')}</h3>
                </div>
                {!isEditing && !isReviewStatus && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest transition-colors group"
                    style={{ color: ps.text }}
                  >
                    <Edit3 size={11} className="group-hover:rotate-12 transition-transform" />
                    <span>{t('edit')}</span>
                  </button>
                )}
                {isEditing && (
                  <div className="flex items-center gap-4">
                    <button
                      onClick={handleSave}
                      disabled={isSaving}
                      className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-emerald-500 hover:text-emerald-400 transition-colors"
                    >
                      <Check size={12} />
                      <span>{isSaving ? '...' : t('save')}</span>
                    </button>
                    <button
                      onClick={handleDiscard}
                      className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-400 transition-colors"
                    >
                      <RotateCcw size={11} />
                      <span>{t('cancel')}</span>
                    </button>
                  </div>
                )}
              </div>


              {isEditing ? (
                <textarea
                  autoFocus
                  id="ticket-description-edit"
                  name="description"
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  className="w-full min-h-[120px] bg-white/[0.03] border border-white/10 rounded-2xl p-6 text-slate-200 text-sm leading-relaxed focus:outline-none focus:border-primary/50 focus:bg-white/[0.05] transition-all custom-scrollbar resize-none font-medium whitespace-pre-wrap"
                  placeholder={t('descriptionPlaceholder')}
                />
              ) : (
                <div
                  onClick={() => !isReviewStatus && setIsEditing(true)}
                  className={`${isReviewStatus ? 'cursor-default' : 'cursor-pointer'} text-slate-300 text-[13px] leading-relaxed whitespace-pre-wrap font-medium break-all md:break-words overflow-wrap-anywhere max-w-full p-2 -m-2 rounded-xl hover:bg-white/[0.02] transition-colors min-h-[1.5em]`}
                >
                  {ticket.description || t('noDescription')}
                </div>
              )}

            </motion.div>

            {/* Affected Endpoints / UX References Section */}
            {(!!ticket.backendEndpoints?.length || (Array.isArray(ticket.figmaReference) ? !!ticket.figmaReference.length : !!ticket.figmaReference)) && (
              <motion.div layout="position" className="space-y-4 pt-8 border-t border-white/5">
                <div className="flex flex-col gap-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Backend Endpoints */}
                    {(!!ticket.backendEndpoints?.length || !!ticket.backendEndpoint) && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 rounded-lg bg-blue-500/10 border border-blue-500/20">
                            <Globe size={14} className="text-blue-400" />
                          </div>
                          <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-400 font-mono italic">
                            {t('backendEndpoints')}
                          </h3>
                        </div>
                        <div className="space-y-2">
                          {ticket.backendEndpoints?.length > 0 ? (
                            ticket.backendEndpoints.map((endpoint, idx) => (
                              <div key={idx} className="group/endpoint relative flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/5 hover:bg-white/[0.06] transition-all">
                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500/40 group-hover/endpoint:bg-blue-500 transition-colors" />
                                <code className="text-[11px] font-mono font-bold text-slate-300 break-all">
                                  {endpoint}
                                </code>
                              </div>
                            ))
                          ) : (
                            <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/5">
                              <div className="w-1.5 h-1.5 rounded-full bg-blue-500/40" />
                              <code className="text-[11px] font-mono font-bold text-slate-300 break-all">
                                {ticket.backendEndpoint}
                              </code>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* UX References (Figma) */}
                    {(Array.isArray(ticket.figmaReference) ? !!ticket.figmaReference.length : !!ticket.figmaReference) && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 rounded-lg bg-fuchsia-500/10 border border-fuchsia-500/20">
                            <LinkIcon size={14} className="text-fuchsia-400" />
                          </div>
                          <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-fuchsia-400 font-mono italic">
                            {t('uxReferences')}
                          </h3>
                        </div>
                        <div className="grid grid-cols-1 gap-3">
                          {Array.isArray(ticket.figmaReference) ? (
                            ticket.figmaReference.map((ref, idx) => (
                              <motion.a
                                key={idx}
                                href={ref.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                whileHover={{ x: 4, backgroundColor: 'rgba(217, 70, 239, 0.05)' }}
                                className="group/ref flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-white/5 hover:border-fuchsia-500/30 transition-all cursor-pointer overflow-hidden"
                              >
                                <div className="flex flex-col min-w-0 pr-4">
                                  <span className="text-[8px] text-fuchsia-400 uppercase font-black tracking-widest mb-0.5">{ref.name}</span>
                                  <span className="text-slate-200 text-[11px] font-bold truncate group-hover/ref:text-white transition-colors">{ref.link}</span>
                                </div>
                                <div className="p-1.5 rounded-lg bg-white/5 text-slate-500 group-hover/ref:bg-fuchsia-500 group-hover/ref:text-white transition-all">
                                  <ExternalLink size={12} />
                                </div>
                              </motion.a>
                            ))
                          ) : (
                            <motion.a
                              href={ticket.figmaReference}
                              target="_blank"
                              rel="noopener noreferrer"
                              whileHover={{ x: 4, backgroundColor: 'rgba(217, 70, 239, 0.05)' }}
                              className="group/ref flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-white/5 hover:border-fuchsia-500/30 transition-all cursor-pointer overflow-hidden"
                            >
                              <div className="flex flex-col min-w-0 pr-4">
                                <span className="text-[8px] text-fuchsia-400 uppercase font-black tracking-widest mb-0.5">{t('visualReference')}</span>
                                <span className="text-slate-200 text-[11px] font-bold truncate group-hover/ref:text-white transition-colors">Figma Design Link</span>
                              </div>
                              <div className="p-1.5 rounded-lg bg-white/5 text-slate-500 group-hover/ref:bg-fuchsia-500 group-hover/ref:text-white transition-all">
                                <ExternalLink size={12} />
                              </div>
                            </motion.a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Workflow Steps Section */}
            {ticket.steps && ticket.steps.length > 0 && (
              <motion.div layout="position" className="space-y-6 pt-8 border-t border-white/5">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-primary/10 border border-primary/20">
                    <ListTree size={14} className="text-primary" />
                  </div>
                  <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-primary font-mono italic">
                    {t('workflowSteps') || 'Workflow Steps'}
                  </h3>
                </div>

                <div className="space-y-4 relative pl-4 border-l-2 border-white/5 ml-2">
                  {[
                    { 
                      message: ticket.message, 
                      screenshot: ticket.screenshot, 
                      url: ticket.url, 
                      timestamp: ticket.timestamp || ticket.createdAt 
                    },
                    ...(ticket.steps || [])
                  ].map((step, idx) => (
                    <div key={idx} className="relative group/step">
                      {/* Timeline Dot */}
                      <div className="absolute -left-[25px] top-1.5 w-4 h-4 rounded-full bg-slate-900 border-2 border-primary/40 flex items-center justify-center z-10">
                        <span className="text-[8px] font-black text-primary">{idx + 1}</span>
                      </div>
                      
                      <div className="p-4 rounded-2xl bg-white/[0.03] border border-white/5 group-hover/step:bg-white/[0.06] group-hover/step:border-white/10 transition-all">
                        <div className="flex flex-col md:flex-row gap-4">
                          {step.screenshot && (
                            <div 
                              className="w-full md:w-32 aspect-video rounded-xl overflow-hidden border border-white/10 shrink-0 cursor-zoom-in hover:scale-[1.02] transition-transform"
                              onClick={() => onOpenEvidence(step.screenshot)}
                            >
                              <img src={step.screenshot} className="w-full h-full object-cover" alt={`Step ${idx + 1}`} />
                            </div>
                          )}
                          <div className="flex-1 min-w-0 space-y-2">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[10px] font-bold text-slate-500">
                                {step.timestamp ? new Date(step.timestamp).toLocaleTimeString(lang === 'es' ? 'es-ES' : 'en-US', { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                              </span>
                              {step.url && (
                                <a 
                                  href={step.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-[9px] font-black text-primary/60 hover:text-primary transition-colors truncate max-w-[150px]"
                                >
                                  {step.url}
                                </a>
                              )}
                            </div>
                            <p className="text-[12px] text-slate-300 font-medium whitespace-pre-wrap leading-relaxed">
                              {step.message}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Original Screenshot Toggle - Only show if not resolved Comparison View is NOT available and NOT a multi-step workflow */}
            {ticket.hasScreenshot && !ticket.hasResolvedScreenshot && !ticket.resolvedScreenshot && !evidenceCache[`${ticket._id}_resolved`] && (!ticket.steps || ticket.steps.length === 0) && (
              <motion.div layout="position" className="space-y-4 pt-8 border-t border-white/5">
                <div
                  onClick={() => handleFetchEvidence()}
                  className="group/ev cursor-pointer relative overflow-hidden rounded-2xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.05] transition-all p-5 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center group-hover/ev:scale-105 group-hover/ev:rotate-3 transition-all duration-500 border shadow-inner"
                      style={{
                        backgroundColor: ps.bgSubtle,
                        borderColor: ps.borderSubtle,
                        color: ps.text
                      }}
                    >
                      {isLoadingScreenshot ? (
                        <div className="w-4 h-4 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
                      ) : (
                        <Layers size={18} />
                      )}
                    </div>
                    <div>
                      <h3 className="text-[8px] font-black uppercase tracking-widest text-slate-500 font-mono italic mb-0.5">
                        {t('visualReference')}
                      </h3>
                      <p className="text-slate-200 text-[11px] font-bold tracking-tight">
                        {isLoadingScreenshot ? t('fetchingEvidence') : t('highResCapture')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-slate-500 transition-colors"
                      style={{
                        '--hover-bg': ps.text,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = ps.text;
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                        e.currentTarget.style.color = '#64748b';
                      }}
                    >
                      <Maximize2 size={13} />
                    </motion.div>
                  </div>

                  {/* Background Glow - Matches Priority */}
                  <div
                    className="absolute top-0 right-0 w-32 h-32 blur-[60px] rounded-full translate-x-10 -translate-y-10 transition-all duration-700 opacity-10 group-hover/ev:opacity-20"
                    style={{ backgroundColor: ps.text }}
                  />
                </div>
              </motion.div>
            )}

            {/* Manual Evidence Upload Zone - Only for Review status without INITIAL capture */}
            {ticket.status === 'review' && !ticket.hasScreenshot && !ticket.screenshot && (
              <motion.div layout="position" className="space-y-4 pt-8 border-t border-white/5">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                    <Camera size={14} className="text-indigo-400" />
                  </div>
                  <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-400 font-mono italic">
                    {t('uploadManualEvidence')}
                  </h3>
                </div>

                <div
                  className="group/upload relative overflow-hidden rounded-[24px] border-2 border-dashed border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-indigo-500/40 transition-all p-8 flex flex-col items-center justify-center gap-4 cursor-pointer"
                  onClick={() => !isUploadingEvidence && document.getElementById('manual-evidence-input').click()}
                >
                  <input
                    id="manual-evidence-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleUploadEvidence}
                  />

                  <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-400 group-hover/upload:scale-110 group-hover/upload:rotate-3 transition-transform duration-500">
                    {isUploadingEvidence ? (
                      <div className="w-6 h-6 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
                    ) : (
                      <Plus size={24} />
                    )}
                  </div>

                  <div className="text-center">
                    <p className="text-slate-200 text-sm font-bold tracking-tight mb-1">
                      {isUploadingEvidence ? t('uploadingEvidence') : t('selectImage')}
                    </p>
                    <p className="text-slate-500 text-[10px] uppercase font-black tracking-widest font-mono">
                      {t('uploadManualEvidenceDesc')}
                    </p>
                  </div>

                  {/* Decorative background glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-transparent opacity-0 group-hover/upload:opacity-100 transition-opacity" />
                </div>
              </motion.div>
            )}

            {/* Evidence Comparison Section (Premium Side-by-Side) */}
            {(ticket.hasResolvedScreenshot || ticket.resolvedScreenshot || evidenceCache[`${ticket._id}_resolved`]) && (
              <motion.div layout="position" className="space-y-6 pt-8 border-t border-white/5">
                <div className="flex items-center gap-2">
                  <div className={`p-1.5 rounded-lg border ${ticket.hasScreenshot ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-indigo-500/10 border-indigo-500/20'}`}>
                    <Camera size={14} className={ticket.hasScreenshot ? 'text-emerald-400' : 'text-indigo-400'} />
                  </div>
                  <h3 className={`text-[10px] font-black uppercase tracking-[0.2em] font-mono italic ${ticket.hasScreenshot ? 'text-emerald-400' : 'text-indigo-400'}`}>
                    {ticket.hasScreenshot ? t('evidenceComparison') : t('validatedEvidence')}
                  </h3>
                </div>

                <div className={`grid gap-6 pb-2 ${ticket.hasScreenshot ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                  {/* Before - Only if it exists */}
                  {ticket.hasScreenshot && (
                    <div className="space-y-3 group/comp">
                      <div className="flex items-center justify-between px-1">
                        <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5 font-mono">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-500/40" />
                          {t('evidenceBefore')}
                        </span>
                      </div>
                      <div
                        className="relative aspect-video rounded-[24px] border border-white/5 bg-slate-900/40 overflow-hidden cursor-zoom-in hover:border-white/20 transition-all duration-500 shadow-lg"
                        onClick={() => !isLoadingScreenshot && handleFetchEvidence()}
                      >
                        {/* Before Skeleton */}
                        {isLoadingScreenshot && (
                          <div className="absolute inset-0 skeleton-shimmer z-10" />
                        )}

                        <img
                          src={ticket.screenshot || (evidenceCache[ticket._id])}
                          alt="Before"
                          onLoad={() => setIsLoadingScreenshot(false)}
                          className={`w-full h-full object-contain object-center transition-all duration-700 ${isLoadingScreenshot ? 'opacity-0 scale-110' : 'opacity-100 scale-100 group-hover/comp:scale-105'}`}
                        />

                        {!isLoadingScreenshot && (
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent opacity-0 group-hover/comp:opacity-100 transition-opacity flex items-end p-5">
                            <div className="flex items-center gap-2 text-[10px] text-white font-black uppercase tracking-widest bg-white/10 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/10">
                              <Maximize2 size={12} />
                              {t('viewFull')}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* After */}
                  <div className="space-y-3 group/comp-after">
                    <div className="flex items-center justify-between px-1">
                      <span className={`text-[9px] font-black uppercase tracking-widest flex items-center gap-1.5 font-mono ${ticket.hasScreenshot ? 'text-emerald-500' : 'text-indigo-500'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${ticket.hasScreenshot ? 'bg-emerald-500' : 'bg-indigo-500'}`} />
                        {ticket.hasScreenshot ? t('evidenceAfter') : t('validatedEvidence')}
                      </span>
                    </div>
                    <div
                      className={`relative aspect-video rounded-[24px] border overflow-hidden cursor-zoom-in transition-all duration-500 shadow-lg ${ticket.hasScreenshot
                          ? 'border-emerald-500/20 bg-emerald-500/[0.03] hover:border-emerald-500/40 shadow-emerald-500/5'
                          : 'border-indigo-500/20 bg-indigo-500/[0.03] hover:border-indigo-500/40 shadow-indigo-500/5'
                        }`}
                      onClick={() => (isAfterScreenshotLoaded || evidenceCache[`${ticket._id}_resolved`]) && handleFetchEvidence(ticket.resolvedScreenshot || evidenceCache[`${ticket._id}_resolved`])}
                    >
                      {/* After Skeleton */}
                      {(!isAfterScreenshotLoaded && !evidenceCache[`${ticket._id}_resolved`]) && (
                        <div className="absolute inset-0 skeleton-shimmer z-10" />
                      )}

                      <img
                        src={ticket.resolvedScreenshot || evidenceCache[`${ticket._id}_resolved`]}
                        alt="After"
                        onLoad={() => setIsAfterScreenshotLoaded(true)}
                        className={`w-full h-full object-contain object-center transition-all duration-700 ${(!isAfterScreenshotLoaded && !evidenceCache[`${ticket._id}_resolved`]) ? 'opacity-0 scale-110' : 'opacity-100 scale-100 group-hover/comp-after:scale-105'}`}
                      />

                      {(isAfterScreenshotLoaded || evidenceCache[`${ticket._id}_resolved`]) && (
                        <div className={`absolute inset-0 bg-gradient-to-t via-transparent to-transparent opacity-0 group-hover/comp-after:opacity-100 transition-opacity flex items-end p-5 ${ticket.hasScreenshot ? 'from-emerald-950/80' : 'from-indigo-950/80'}`}>
                          <div className={`flex items-center gap-2 text-[10px] text-white font-black uppercase tracking-widest backdrop-blur-md px-3 py-1.5 rounded-full border ${ticket.hasScreenshot ? 'bg-emerald-500/40 border-emerald-500/40' : 'bg-indigo-500/40 border-indigo-500/40'}`}>
                            <Maximize2 size={12} />
                            {t('viewFull')}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Corrections Section (Active and History) */}
            {ticket.corrections && (Array.isArray(ticket.corrections) ? ticket.corrections.length > 0 : !!ticket.corrections) && (
              <motion.div layout="position" className="space-y-6 mb-8 pt-8 border-t border-white/5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={14} className="text-red-400" />
                    <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 font-mono italic">
                      {t('correctionHistory')}
                    </h3>
                  </div>
                </div>

                 <div className="space-y-4">
                  {(Array.isArray(ticket.corrections) ? ticket.corrections : [{ text: ticket.corrections, status: 'pending', createdAt: ticket.createdAt }]).map((corr, idx, arr) => {
                    const isLatest = idx === arr.length - 1;
                    const isPending = corr.status === 'pending';
                    const isApplied = corr.status === 'applied' || corr.appliedBy === 'ai';

                    return (
                      <div
                        key={corr.id || idx}
                        className={`relative p-5 rounded-[24px] border backdrop-blur-xl transition-all duration-500 overflow-hidden group/corr ${isPending
                            ? 'bg-red-500/10 border-red-500/30 hover:bg-red-500/20 shadow-lg shadow-red-500/10'
                            : isApplied 
                              ? 'bg-indigo-500/10 border-indigo-500/30 hover:bg-indigo-500/20 shadow-lg shadow-indigo-500/10'
                              : 'bg-white/[0.03] border-white/10 hover:bg-white/[0.05]'
                          }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-xl flex items-center justify-center border ${
                              isPending ? 'bg-red-500/20 border-red-500/20 text-red-400' : 
                              isApplied ? 'bg-indigo-500/20 border-indigo-500/20 text-indigo-400' :
                              'bg-slate-500/10 border-white/10 text-slate-400'
                            }`}>
                              {isApplied ? <Bot size={14} /> : <MessageSquare size={14} />}
                            </div>
                            <div className="flex flex-col">
                              <span className={`text-[8px] font-black uppercase tracking-widest ${
                                isPending ? 'text-red-400' : 
                                isApplied ? 'text-indigo-400' : 
                                'text-slate-500'
                              }`}>
                                {isPending ? t('pendingFix') : (isApplied ? t('appliedFix') : t('resolvedFix'))}
                              </span>
                              <span className="text-[10px] font-bold text-slate-400">
                                {new Date(corr.createdAt).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          </div>

                          {corr.author && (
                            <div className="flex items-center gap-2 bg-white/5 px-2 py-1 rounded-lg border border-white/5">
                              <div className="w-4 h-4 rounded-full border border-white/10 overflow-hidden">
                                <img src={corr.author.avatar || '/default-avatar.png'} alt="" className="w-full h-full object-cover" />
                              </div>
                              <span className="text-[9px] font-bold text-slate-400">{corr.author.name}</span>
                            </div>
                          )}

                          {isApplied && (
                            <div className="flex items-center gap-2 bg-indigo-500/10 px-2 py-1 rounded-lg border border-indigo-500/20">
                              <Sparkles size={10} className="text-indigo-400 animate-pulse" />
                              <span className="text-[9px] font-black uppercase tracking-tighter text-indigo-400">{t('aiVerified')}</span>
                            </div>
                          )}

                          {/* Action Buttons: Only show if NOT in review status AND the correction is still pending (and NOT applied) */}
                          {!isReviewStatus && isPending && !isApplied && (
                            <div className="flex items-center gap-1.5 opacity-0 group-hover/corr:opacity-100 transition-all duration-300">
                            {editingCorrectionId === (corr.id || corr._id) ? (
                              <>
                                <button
                                  onClick={() => handleSaveEditCorrection(corr.id || corr._id)}
                                  className="w-7 h-7 rounded-lg bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 flex items-center justify-center hover:bg-emerald-500/30 transition-all"
                                >
                                  <Check size={12} strokeWidth={3} />
                                </button>
                                <button
                                  onClick={() => setEditingCorrectionId(null)}
                                  className="w-7 h-7 rounded-lg bg-white/5 border border-white/10 text-slate-400 flex items-center justify-center hover:bg-white/10 transition-all"
                                >
                                  <X size={12} />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => {
                                    setEditingCorrectionId(corr.id || corr._id);
                                    setEditingCorrectionText(corr.text);
                                  }}
                                  className="w-7 h-7 rounded-lg bg-white/5 border border-white/10 text-slate-400 flex items-center justify-center hover:bg-white/10 hover:text-white transition-all"
                                >
                                  <Pencil size={12} />
                                </button>
                                <button
                                  onClick={() => handleDeleteCorrection(corr.id || corr._id)}
                                  className="w-7 h-7 rounded-lg bg-white/5 border border-white/10 text-slate-400 flex items-center justify-center hover:bg-red-500/20 hover:text-red-400 hover:border-red-500/30 transition-all"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </>
                            )}
                          </div>
                          )}
                        </div>

                        <div className="pl-1">
                          {editingCorrectionId === (corr.id || corr._id) ? (
                            <textarea
                              value={editingCorrectionText}
                              onChange={(e) => setEditingCorrectionText(e.target.value)}
                              autoFocus
                              className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-[13px] text-white focus:outline-none focus:border-red-500/30 font-bold italic min-h-[80px] resize-none"
                            />
                          ) : (
                            <p className="text-[13px] text-slate-200 leading-relaxed font-bold italic">
                              "{corr.text}"
                            </p>
                          )}

                          {corr.fixDescription && !ticket.aiFixSummary && (
                            <div className="mt-4 pt-4 border-t border-white/5 space-y-2">
                              <span className="text-[8px] font-black uppercase tracking-widest text-emerald-500 flex items-center gap-1.5">
                                <Check size={10} />
                                {t('fixDescriptionLabel')}
                              </span>
                              <p className="text-[12px] text-slate-400 font-medium bg-emerald-500/5 p-3 rounded-xl border border-emerald-500/10">
                                {corr.fixDescription}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Background Glow for pending */}
                        {isPending && (
                          <div className="absolute -bottom-8 -right-8 w-24 h-24 bg-red-500/20 blur-[40px] rounded-full animate-pulse opacity-50" />
                        )}
                      </div>
                    );
                  }).reverse()}
                </div>
              </motion.div>
            )}

            {ticket.aiFixSummary && (
              <motion.div layout="position" className="space-y-4 pt-8 border-t border-white/5">
                <div className="p-6 rounded-[20px] bg-emerald-500/5 border border-emerald-500/10 relative overflow-hidden group/fix">
                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 size={14} className="text-emerald-400" />
                      <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-400 font-mono italic">
                        {t('aiFixSummary')}
                      </h3>
                    </div>
                    <p className="text-[13px] text-slate-300 leading-relaxed font-medium">
                      {ticket.aiFixSummary}
                    </p>
                  </div>
                  <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 blur-[50px] rounded-full translate-x-16 -translate-y-16 group-hover/fix:bg-emerald-500/20 transition-all duration-700" />
                </div>
              </motion.div>
            )}

            {/* Original and Updated Instruction (Read-only) Section - Hide if multi-step workflow as it is shown in Step 1 */}
            {(!ticket.steps || ticket.steps.length === 0) && (
              <motion.div layout="position" className="space-y-4 pt-8 border-t border-white/5">
                <div className="space-y-4">
                  <div className="flex items-center justify-between px-1">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-lg bg-slate-500/10 flex items-center justify-center">
                        <MessageSquare size={10} className="text-slate-500" />
                      </div>
                      <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 font-display">
                        {t('originalComment')}
                      </h3>
                    </div>
                  </div>
                  
                  <div className="relative group/comment">
                    <div className="absolute -inset-1 bg-gradient-to-r from-slate-500/10 to-transparent rounded-[26px] blur-sm opacity-0 group-hover/comment:opacity-100 transition-opacity duration-500" />
                    <div className="relative p-6 rounded-[24px] bg-white/[0.03] border border-white/5 backdrop-blur-xl overflow-hidden min-h-[100px] flex flex-col justify-center">
                      {/* Decorative Quote Mark */}
                      <div className="absolute -top-2 -right-2 text-white/[0.03] select-none pointer-events-none rotate-12">
                        <Quote size={80} />
                      </div>
                      
                      <div className="relative z-10 text-slate-300 text-[13px] leading-relaxed whitespace-pre-wrap font-medium break-words overflow-wrap-anywhere">
                        {ticket.message || ticket.description || ticket.title || t('noOriginalMessage')}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Planning Section */}
            {ticket.planning && ticket.planning.complejidad && (
              <motion.div layout="position" className="space-y-4 pt-8 border-t border-white/5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap size={14} className="text-amber-400" />
                    <h3 className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500 font-mono italic flex items-center gap-2">
                      {t('planningAI')}
                      {ticket.planning.tipo && (
                        <Tooltip content={t('typeTooltip')}>
                          <span
                            className="ml-2 px-2 py-0.5 rounded-md text-[8px] font-black bg-white/5 border border-white/5 text-slate-400 uppercase tracking-tighter cursor-help flex items-center gap-1 hover:bg-white/10 transition-colors shrink-0"
                          >
                            {t(ticket.planning.tipo.toLowerCase().replace(/-(.)/g, (m, c) => c.toUpperCase()).replace(/^ai/, 'ai'))}
                            <Info size={8} />
                          </span>
                        </Tooltip>
                      )}
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <Tooltip content={t('scoreTooltip')}>
                      <div
                        className="flex flex-col items-end cursor-help group/tip"
                      >
                        <span className="text-[8px] font-black uppercase text-slate-600 tracking-tighter mb-0.5 flex items-center gap-1">
                          {t('score')}
                          <Info size={8} className="opacity-0 group-hover/tip:opacity-100 transition-opacity" />
                        </span>
                        <span className="text-xs font-black px-2 py-1 rounded-lg bg-primary/20 text-primary border border-primary/20 group-hover/tip:border-primary/40 transition-colors">
                          {ticket.planning.score}
                        </span>
                      </div>
                    </Tooltip>
                    <Tooltip content={t('priorityTooltip')}>
                      <div
                        className="flex flex-col items-end ml-2 cursor-help group/tip"
                      >
                        <span className="text-[8px] font-black uppercase text-slate-600 tracking-tighter mb-0.5 flex items-center gap-1">
                          {t('priority')}
                          <Info size={8} className="opacity-0 group-hover/tip:opacity-100 transition-opacity" />
                        </span>
                        <span className="text-xs font-black px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 group-hover/tip:border-emerald-500/40 transition-colors">
                          {Number(ticket.planning.priority).toFixed(2)}
                        </span>
                      </div>
                    </Tooltip>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: t('complexity'), value: ticket.planning.complejidad, color: '#6366f1', tooltip: t('complexityTooltip') },
                    { label: t('uncertainty'), value: ticket.planning.incertidumbre, color: '#a855f7', tooltip: t('uncertaintyTooltip') },
                    { label: t('impact'), value: ticket.planning.impacto, color: '#f59e0b', tooltip: t('impactTooltip') },
                    { label: t('risk'), value: ticket.planning.riesgo, color: '#f43f5e', tooltip: t('riskTooltip') }
                  ].map((item, idx) => (
                    <Tooltip key={idx} content={item.tooltip}>
                      <div
                        className="p-4 rounded-[20px] bg-white/[0.02] border border-white/5 flex flex-col gap-2 group/metric hover:bg-white/[0.04] transition-all cursor-help w-full"
                      >
                        <span className="text-[8px] font-black uppercase text-slate-500 tracking-[0.1em] flex items-center justify-between">
                          {item.label}
                          <Info size={10} className="opacity-20 group-hover/metric:opacity-100 transition-opacity" />
                        </span>
                        <div className="flex items-end justify-between gap-2">
                          <span className="text-xl font-black text-white leading-none">{item.value}</span>
                          <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden mb-1">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${(item.value / 3) * 100}%` }}
                              className="h-full rounded-full"
                              style={{ backgroundColor: item.color }}
                            />
                          </div>
                        </div>
                      </div>
                    </Tooltip>
                  ))}
                </div>

                {ticket.planning.aiAnalysis && (
                  <div className="p-6 rounded-[20px] bg-primary/5 border border-primary/10 relative overflow-hidden group/analysis">
                    <div className="relative z-10">
                      <p className="text-[13px] text-slate-300 leading-relaxed font-medium">
                        <span className="font-black text-primary mr-2 uppercase text-[10px] tracking-widest font-mono italic">{t('aiAnalysis')}:</span>
                        {ticket.planning.aiAnalysis}
                      </p>
                    </div>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 blur-[50px] rounded-full translate-x-16 -translate-y-16 group-hover/analysis:bg-primary/20 transition-all duration-700" />
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* Minimalist Animated Scroll Indicator - Sibling of scroller for stable positioning */}
          <AnimatePresence>
            {showScrollArrow && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{
                  opacity: 0.8,
                  y: [0, 8, 0]
                }}
                exit={{ opacity: 0 }}
                transition={{
                  y: {
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut"
                  },
                  opacity: { duration: 0.2 }
                }}
                className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[60] pointer-events-none"
                style={{ color: getPriorityHex(editedPriority) }}
              >
                <ChevronDown size={28} strokeWidth={2.5} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Very Compact Footer */}
        <div className="p-4 px-8 border-t border-white/5 bg-[#0a0f18]/20 flex justify-end items-center gap-3 flex-none">
          {/* Delete Button (Available in all statuses) */}
          {!isEditing && (
            <div className="mr-auto">
              <button
                onClick={handleDeleteTicket}
                className="p-2.5 text-rose-500 hover:text-white bg-rose-500/10 hover:bg-rose-500 rounded-xl transition-all border border-rose-500/20 active:scale-95 group shadow-lg shadow-rose-500/5"
                title={t('confirmDeleteTicket')}
              >
                <Trash2 size={18} className="group-hover:rotate-6 transition-transform" />
              </button>
            </div>
          )}
          {!isEditing && (ticket.status === 'todo' || ticket.status === 'bug_fixing') && (
            <button
              onClick={() => {
                setTargetStatus('applied');
                setIsConfirmingStatus(true);
              }}
              className="px-6 py-2 text-white rounded-xl text-[9px] font-black uppercase tracking-[0.2em] transition-all border border-white/5 shadow-lg hover:brightness-125 bg-blue-600/40 hover:bg-blue-600/60"
            >
              {t('appliedLocal')}
            </button>
          )}

           {/* Transition Buttons (Todo, Bugs, Applied, In Progress -> Review) */}
          {!isEditing && (ticket.status === 'todo' || ticket.status === 'bug_fixing' || ticket.status === 'applied' || ticket.status === 'in_progress') && (
            <button
              onClick={() => {
                setTargetStatus('review');
                setIsConfirmingStatus(true);
              }}
              className={`px-6 py-2 text-white rounded-xl text-[9px] font-black uppercase tracking-[0.2em] transition-all border border-white/5 shadow-lg hover:brightness-125 ${ticket.status === 'applied' ? 'bg-indigo-600/40 hover:bg-indigo-600/60' : 'bg-purple-600/40 hover:bg-purple-600/60'}`}
            >
              {ticket.status === 'applied' ? t('markAsLive') : t('moveToReview')}
            </button>
          )}

          {!isEditing && (ticket.status === 'review' || ticket.status === 'ready_for_review') && (
            <>
              <button
                onClick={() => {
                  const isBug = ticket.type === 'bug' || ticket.originalStatus === 'bug_fixing';
                  setTargetStatus(isBug ? 'bug_fixing' : 'todo');
                  setIsConfirmingStatus(true);
                }}
                className={`px-6 py-2 text-white rounded-xl text-[9px] font-black uppercase tracking-[0.2em] transition-all border border-white/5 shadow-lg hover:brightness-125 ${(ticket.type === 'bug' || ticket.originalStatus === 'bug_fixing') ? 'bg-rose-600/40 hover:bg-rose-600/60' : 'bg-amber-600/40 hover:bg-amber-600/60'
                  }`}
              >
                {(ticket.type === 'bug' || ticket.originalStatus === 'bug_fixing') ? t('returnToBugs') : t('returnToPending')}
              </button>
              <button
                onClick={() => {
                  setTargetStatus('done');
                  setIsConfirmingStatus(true);
                }}
                className="px-6 py-2 text-white rounded-xl text-[9px] font-black uppercase tracking-[0.2em] transition-all border border-white/5 shadow-lg hover:brightness-125 bg-emerald-600/40 hover:bg-emerald-600/60"
              >
                {t('completeTicket')}
              </button>
            </>
          )}

          <button
            onClick={onClose}
            className="px-6 py-2 text-white rounded-xl text-[9px] font-black uppercase tracking-[0.2em] transition-all border border-white/5 shadow-lg hover:brightness-125"
            style={{ backgroundColor: ps.text }}
          >
            {t('close')}
          </button>
        </div>

        <StatusConfirmationModal
          isOpen={isConfirmingStatus}
          targetStatus={targetStatus}
          onConfirm={handleStatusChange}
          onCancel={() => {
            setIsConfirmingStatus(false);
            setTargetStatus(null);
            setCorrectionsText("");
            setFixDescription("");
          }}
          corrections={correctionsText}
          setCorrections={setCorrectionsText}
          isReverting={isRevertingStatus}
          isResolvingCorrection={isResolvingCorrection}
          fixDescription={fixDescription}
          setFixDescription={setFixDescription}
        />

        {/* Action Confirmation Modal */}
        <ConfirmActionModal
          isOpen={!!confirmModal}
          onClose={() => setConfirmModal(null)}
          onConfirm={() => confirmModal?.onConfirm()}
          title={confirmModal?.title}
          message={confirmModal?.message}
          type={confirmModal?.type}
        />

      </motion.div>
    </motion.div>
  );
});

const EMPTY_ARRAY = [];
const fetcher = url => api.get(url).then(res => res.data);
const SWR_STALE_CONFIG = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  dedupingInterval: 3600000, // 1 hour
};

const Board = () => {
  const { lang, t } = useLanguage();
  const { activeProject, activeWorkspace, activeProjectId, setActiveProjectId, projects: teams, filteredProjects, canAccess, loading, isInitialized } = useProject();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const STATUS_COLUMNS = React.useMemo(() => get_status_columns(t), [t]);

  const hasUxMembers = React.useMemo(() => {
    return (activeProject?.members || []).some(m => m.role && /ux|ui|design/i.test(m.role));
  }, [activeProject]);

  // High-Performance Collision Registry
  // Map of ticketId -> { rect, lastUpdated }
  const collisionRegistry = useRef(new Map());

  const registerTicket = useCallback((id, rect) => {
    if (!id) return;
    if (!rect) {
      collisionRegistry.current.delete(id);
    } else {
      collisionRegistry.current.set(id, { rect, lastUpdated: Date.now() });
    }
  }, []);

  useEffect(() => {
    if (isInitialized && !loading && !canAccess('/board')) {
      navigate('/projects');
    }
  }, [canAccess, navigate, loading, isInitialized]);

  useEffect(() => {
    // Expert layout optimization: Remove parent padding and overflow to ensure only columns scroll
    const main = document.querySelector('main');
    if (main) {
      const originalPadding = main.style.padding;
      const originalOverflow = main.style.overflow;
      main.style.padding = '0';
      main.style.overflow = 'hidden';
      return () => {
        main.style.padding = originalPadding;
        main.style.overflow = originalOverflow;
      };
    }
  }, []);

  const [selectedSprint, setSelectedSprint] = useState(null);
  const [showUiImprovements, setShowUiImprovements] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [selectedTags, setSelectedTags] = useState('all');
  const [selectedCreator, setSelectedCreator] = useState('all');
  const [selectedMemberType, setSelectedMemberType] = useState('creator');
  const [selectedDateRange, setSelectedDateRange] = useState({ type: 'all', start: null, end: null });
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterBugsOnly, setFilterBugsOnly] = useState(false);
  const [columns, setColumns] = useState(['todo', 'review', 'bug_fixing', 'done']);
  const [evidenceCache, setEvidenceCache] = useState({});
  const [evidenceUrl, setEvidenceUrl] = useState(null);
  const isEvidenceOpen = !!evidenceUrl;
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isFullEvidenceLoading, setIsFullEvidenceLoading] = useState(true);
  const [apiSearchQuery, setApiSearchQuery] = useState(searchParams.get('search') || '');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchParams.get('search') || '');
  const [isPending, startTransition] = React.useTransition();

  // Sync search from URL params if changed externally
  useEffect(() => {
    const search = searchParams.get('search');
    if (search && search !== searchQuery) {
      setSearchQuery(search);
    }
  }, [searchParams]);
  

  // Close evidence preview or expanded ticket with Escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (isEvidenceOpen) setEvidenceUrl(null);
        if (selectedTicket) setSelectedTicket(null);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isEvidenceOpen, selectedTicket]);





  // Smooth scroll to top when modal or evidence opens
  useEffect(() => {
    if (selectedTicket || isEvidenceOpen) {
      const mainElement = document.querySelector('main');
      if (mainElement) {
        // Instant scroll to top is more performant during heavy component mounts
        mainElement.scrollTo({ top: 0 });
      }
    }
  }, [selectedTicket, isEvidenceOpen]);

  const lastProjectIdRef = useRef(null);
  const initialSelectionMadeRef = useRef(false);

  // --- PAGINATED DATA LAYER ---
  const getInfiniteKey = (columnId) => (pageIndex, previousPageData) => {
    if (!activeProjectId) return null;
    if (previousPageData && !previousPageData.data?.length) return null;

    let url = `/tickets?projectId=${activeProjectId}&boardColumn=${columnId}&page=${pageIndex}&limit=5`;
    if (showUiImprovements) url += `&showUiImprovements=true`;


    // Add filters to key for server-side optimization
    if (selectedSprint === 'backlog' || selectedSprint === null) {
      // Don't send 'backlog' as sprintId to avoid Mongo cast errors
      // Use an alternative parameter or handle on backend if needed
    } else if (selectedSprint && selectedSprint !== 'all') {
      url += `&sprintId=${selectedSprint}`;
    }
    if (selectedPriority !== 'all') url += `&priority=${selectedPriority}`;
    if (selectedTags !== 'all') url += `&tags=${selectedTags}`;
    if (selectedCreator !== 'all') {
      if (selectedMemberType === 'resolver') {
        url += `&resolverEmail=${selectedCreator}`;
      } else {
        url += `&creator=${selectedCreator}`;
      }
    }
    if (apiSearchQuery) url += `&search=${encodeURIComponent(apiSearchQuery)}`;
    if (selectedDateRange.type !== 'all') {
      if (selectedDateRange.start) url += `&startDate=${selectedDateRange.start}`;
      if (selectedDateRange.end) url += `&endDate=${selectedDateRange.end}`;
    }

    return url;
  };

  const todoSWR = useSWRInfinite(getInfiniteKey('todo'), fetcher, { ...SWR_STALE_CONFIG, revalidateFirstPage: false });
  const reviewSWR = useSWRInfinite(getInfiniteKey('review'), fetcher, { ...SWR_STALE_CONFIG, revalidateFirstPage: false });
  const bugFixingSWR = useSWRInfinite(getInfiniteKey('bug_fixing'), fetcher, { ...SWR_STALE_CONFIG, revalidateFirstPage: false });
  const doneSWR = useSWRInfinite(getInfiniteKey('done'), fetcher, { ...SWR_STALE_CONFIG, revalidateFirstPage: false });

  const columnSWRs = useMemo(() => ({
    todo: todoSWR,
    review: reviewSWR,
    bug_fixing: bugFixingSWR,
    done: doneSWR
  }), [todoSWR, reviewSWR, bugFixingSWR, doneSWR]);

  // Flattened tickets for any global logic that needs it
  const sprintStatsKey = useMemo(() => {
    if (!activeProjectId || !selectedSprint || selectedSprint === 'all' || selectedSprint === 'backlog') return null;
    let url = `/projects/${activeProjectId}/sprints/${selectedSprint}/stats?`;
    
    // DECOUPLED: Stats now represent the overall sprint progress regardless of granular board filters
    // This prevents metrics (Traffic Light, Resolved Errors) from jumping when searching or filtering
    if (showUiImprovements) url += `&showUiImprovements=true`;

    return url;
  }, [activeProjectId, selectedSprint, showUiImprovements]);

  const { data: sprintStats, mutate: mutateStats, isValidating: isStatsValidating } = useSWR(sprintStatsKey, fetcher, SWR_STALE_CONFIG);

  // Dedicated endpoint for high-performance dashboard counts (Backlog & Sprint totals)
  const { data: dashboardCounts, mutate: mutateCounts } = useSWR(
    activeProjectId ? `/projects/${activeProjectId}/dashboard/counts?sprintId=${selectedSprint && selectedSprint !== 'all' && selectedSprint !== 'backlog' ? selectedSprint : ''}${showUiImprovements ? '&showUiImprovements=true' : ''}` : null, 
    fetcher,
    SWR_STALE_CONFIG
  );

  // SWR for Sprints
  const { data: sprintsData = [], mutate: mutateSprints } = useSWR(activeProjectId ? `/projects/${activeProjectId}/sprints` : null, fetcher, {
    refreshInterval: 15000
  });
  const sprints = Array.isArray(sprintsData?.data) ? sprintsData.data : (Array.isArray(sprintsData) ? sprintsData : []);

  const tickets = useMemo(() => {
    const all = [
      ...(todoSWR.data?.flatMap(page => page.data) || []),
      ...(reviewSWR.data?.flatMap(page => page.data) || []),
      ...(bugFixingSWR.data?.flatMap(page => page.data) || []),
      ...(doneSWR.data?.flatMap(page => page.data) || [])
    ].filter(Boolean);

    // Deduplicate by _id to prevent UI glitches during transitions or fetch overlaps
    const seen = new Set();
    return all.filter(t => {
      if (seen.has(t._id)) return false;
      seen.add(t._id);
      return true;
    });
  }, [todoSWR.data, reviewSWR.data, bugFixingSWR.data, doneSWR.data]);

  // Debounce the search input to prevent UI jank while typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 600);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Sync debounced search to API search with local matching optimization
  useEffect(() => {
    if (!debouncedSearchQuery) {
      setApiSearchQuery('');
      return;
    }

    // Check if we already have a match locally to avoid redundant backend hits
    const q = debouncedSearchQuery.toLowerCase().trim();
    const cleanQ = q.replace(/^vg-/, '').replace(/^#/, '');
    
    const hasLocalMatch = tickets.some(t => {
      if (!t) return false;
      const ticketKey = String(t.key || '').toLowerCase();
      const ticketId = String(t._id || '').toLowerCase();
      const ticketIdSuffix = ticketId.slice(-6);
      
      return (
        t.title?.toLowerCase().includes(q) ||
        ticketKey.includes(q) ||
        ticketKey.includes(cleanQ) ||
        t.message?.toLowerCase().includes(q) ||
        ticketIdSuffix.includes(q) ||
        ticketIdSuffix.includes(cleanQ) ||
        ticketId.includes(q) ||
        ticketId.includes(cleanQ) ||
        t.creator?.name?.toLowerCase().includes(q) ||
        t.assignee?.name?.toLowerCase().includes(q) ||
        t.resolver?.name?.toLowerCase().includes(q)
      );
    });

    if (!hasLocalMatch) {
      setApiSearchQuery(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery, tickets]);

  // Extract stable mutate functions to avoid re-render cascades
  const todoMutate = todoSWR.mutate;
  const reviewMutate = reviewSWR.mutate;
  const bfMutate = bugFixingSWR.mutate;
  const doneMutate = doneSWR.mutate;

  const mutateTickets = useCallback(() => {
    todoMutate();
    reviewMutate();
    bfMutate();
    doneMutate();
    mutateCounts();
    mutateStats();
  }, [todoMutate, reviewMutate, bfMutate, doneMutate, mutateCounts, mutateStats]);

  // Debounced mutation handler to prevent over-rendering during bursts of events (bulk-update)
  const debouncedRevalidate = useMemo(() => {
    let timeout;
    return () => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        mutateTickets();
      }, 300);
    };
  }, [mutateTickets]);

  const isLimited = !user?.isFounder && !activeWorkspace?.isFoundersTeam && (
    (todoSWR.data?.[0]?.pagination?.total > 5) ||
    (activeProject?.subscription?.status === 'expired') ||
    (activeProject?.subscription?.status === 'unpaid')
  );

  const socketRef = useRef(null);

  // Stabilization Refs for Socket.io
  const columnSWRsRef = useRef(columnSWRs);
  const mutateTicketsRef = useRef(mutateTickets);
  const mutateStatsRef = useRef(mutateStats);
  const mutateCountsRef = useRef(mutateCounts);
  const mutateSprintsRef = useRef(mutateSprints);

  // Sync refs on every render
  useEffect(() => {
    columnSWRsRef.current = columnSWRs;
    mutateTicketsRef.current = mutateTickets;
    mutateStatsRef.current = mutateStats;
    mutateCountsRef.current = mutateCounts;
    mutateSprintsRef.current = mutateSprints;
  });

  // Real-time Socket.io Integration - Persistent Connection
  useEffect(() => {
    if (!activeProjectId || !token) return;

    const socketUrl = import.meta.env.VITE_SOCKET_URL || 'http://localhost:4000';

    const socket = io(socketUrl, {
      auth: { token },
      transports: ['websocket'],
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('join-project', activeProjectId);
    });

    socket.on('annotation-mutated', (data) => {
      // Real-time mutation event logic
      startTransition(() => {
        if (data && data.ticket) {
          // If it's a create, check if it matches the current view filters (simplified to project check)
          const ticketProjectId = data.ticket?.projectId;
          const matchesProject = ticketProjectId && String(ticketProjectId) === String(activeProjectId);
          const id = String(data.id || data.ticket?._id || '');
          
          Object.entries(columnSWRsRef.current).forEach(([columnId, swr]) => {
            if (swr && typeof swr.mutate === 'function') {
              swr.mutate((current) => {
                if (!current) return current;

                if (data.type === 'create') {
                  // Add to column only if it matches current filter logic
                  if (matchesProject && isTicketInColumn(data.ticket, columnId, showUiImprovements, hasUxMembers)) {
                    const updatedPages = [...current];
                    if (updatedPages[0]) {
                      const alreadyIn = (updatedPages[0].data || []).some(t => String(t?._id) === id);
                      if (!alreadyIn) {
                        const limit = updatedPages[0].pagination?.limit || updatedPages[0].limit || 20;
                        const prevTotal = updatedPages[0].pagination?.total ?? updatedPages[0].data?.length ?? 0;
                        
                        updatedPages[0] = {
                          ...updatedPages[0],
                          pagination: {
                            ...updatedPages[0].pagination,
                            total: prevTotal + 1
                          },
                          data: [data.ticket, ...(updatedPages[0].data || [])].slice(0, limit)
                        };
                      }
                    }
                    return updatedPages;
                  }
                  return current;
                }

                if (data.type === 'update') {
                  const belongsHere = isTicketInColumn(data.ticket, columnId, showUiImprovements, hasUxMembers);
                  
                  return current.map((page, index) => {
                    const existingIndex = (page.data || []).findIndex(t => String(t?._id) === id);
                    
                    if (existingIndex !== -1) {
                      // Ticket was in this column
                      if (belongsHere) {
                        // Still belongs here -> Update properties
                        const newData = [...page.data];
                        newData[existingIndex] = { ...newData[existingIndex], ...data.ticket };
                        return { ...page, data: newData };
                      } else {
                        // No longer belongs here -> Remove it and decrement total
                        const isFirstPage = index === 0;
                        const prevTotal = page.pagination?.total ?? page.data?.length ?? 0;
                        
                        return { 
                          ...page, 
                          pagination: isFirstPage ? { 
                            ...page.pagination, 
                            total: Math.max(0, prevTotal - 1) 
                          } : page.pagination,
                          data: page.data.filter(t => String(t?._id) !== id) 
                        };
                      }
                    } else if (belongsHere && index === 0) {
                      // Ticket didn't exist in this column but now belongs here -> Add to top and increment total
                      const limit = page.pagination?.limit || page.limit || 20;
                      const prevTotal = page.pagination?.total ?? page.data?.length ?? 0;
                      
                      return { 
                        ...page, 
                        pagination: {
                          ...page.pagination,
                          total: prevTotal + 1
                        },
                        data: [data.ticket, ...(page.data || [])].slice(0, limit) 
                      };
                    }
                    
                    return page;
                  });
                }

                if (data.type === 'delete') {
                  return current.map((page, index) => {
                    const exists = (page.data || []).some(t => String(t?._id) === id);
                    if (exists) {
                      const isFirstPage = index === 0;
                      const prevTotal = page.pagination?.total ?? page.data?.length ?? 0;
                      return {
                        ...page,
                        pagination: isFirstPage ? {
                          ...page.pagination,
                          total: Math.max(0, prevTotal - 1)
                        } : page.pagination,
                        data: (page.data || []).filter(t => String(t?._id) !== id)
                      };
                    }
                    return page;
                  });
                }

                return current;
              }, false);
            }
          });

          if (selectedSprint) {
            mutateStatsRef.current();
          }
        } else {
          // Bulk operations: backend emits per-ticket updates now; avoid full revalidation.
          if (data?.type === 'bulk-update') {
            mutateCountsRef.current?.();
            mutateStatsRef.current?.();
            return;
          }

          // Sprint metadata changes: patch local sprints cache + refresh small aggregates.
          if (data?.type === 'sprint-update' && data?.sprint && mutateSprintsRef.current) {
            mutateSprintsRef.current((current) => {
              const list = Array.isArray(current?.data) ? current.data : (Array.isArray(current) ? current : []);
              const sprint = data.sprint;
              const next = [...list];
              const idx = next.findIndex(s => String(s?._id) === String(sprint?._id));
              if (idx !== -1) {
                next[idx] = { ...next[idx], ...sprint };
              } else {
                next.unshift(sprint);
              }
              if (Array.isArray(current?.data)) {
                return { ...current, data: next };
              }
              return next;
            }, false);
            mutateCountsRef.current?.();
            mutateStatsRef.current?.();
            return;
          }

          // Malformed/unknown data: fallback to full revalidation.
          debouncedRevalidate();
          mutateStatsRef.current?.();
        }
      });
    });

    socket.on('connect_error', (err) => {
      // Connection error
    });

    return () => {
      if (socket) {
        socket.off(); // Remove all listeners first to stop any pending updates
        socket.disconnect();
        socketRef.current = null;
      }
    };
  }, [activeProjectId, token]); // STABLE: Only depends on project and auth

  useEffect(() => {
    // Reset selection flag when project changes
    if (activeProjectId !== lastProjectIdRef.current) {
      lastProjectIdRef.current = activeProjectId;
      initialSelectionMadeRef.current = false;
      setSelectedSprint(null); // Clear previous selection

      // VG-D93CEB: Reset UI Improvements if the new project has no UX members to avoid stuck filter
      if (showUiImprovements && !hasUxMembers) {
        setShowUiImprovements(false);
      }
    }

    // Don't auto-select if we already did it for this project or if user navigated back
    if (initialSelectionMadeRef.current) return;

    const availableSprints = sprints.filter(s => s.status !== 'closed' && s.status !== 'completed');

    if (availableSprints.length > 0) {
      const now = new Date();
      // 1. Try to find the sprint that contains 'now'
      let current = availableSprints.find(s => {
        const start = new Date(s.startDate);
        const end = new Date(s.endDate);
        return start <= now && end >= now;
      });

      // 2. Fallback: Find the sprint nearest to 'now' among active ones
      if (!current) {
        current = [...availableSprints].sort((a, b) => {
          const startA = new Date(a.startDate);
          const startB = new Date(b.startDate);
          return Math.abs(startA - now) - Math.abs(startB - now);
        })[0];
      }

      if (current) {
        setSelectedSprint(current._id);
        initialSelectionMadeRef.current = true;
      } else if (availableSprints.length === 1) {
        setSelectedSprint(availableSprints[0]._id);
        initialSelectionMadeRef.current = true;
      }
    } else if (sprints.length > 0 && availableSprints.length === 0 && selectedSprint === null) {
      setSelectedSprint('all');
      initialSelectionMadeRef.current = true;
    }
  }, [sprints, activeProjectId, selectedSprint, hasUxMembers, showUiImprovements]);

  const handleOpenTicket = useCallback((ticket) => {
    // Use transition to avoid blocking the main thread during heavy modal render
    startTransition(() => {
      setSelectedTicket(ticket);
      setEvidenceUrl(null);
    });
  }, []);

  const handleCloseTicket = useCallback(() => {
    setSelectedTicket(null);
  }, []);

  const handleOpenEvidence = useCallback((url) => {
    setEvidenceUrl(url);
    setIsFullEvidenceLoading(true);
  }, []);

  const handleOpenTicketById = useCallback(async (id) => {
    if (!id) return;
    
    // 1. Search in local state (todo, review, etc.)
    let foundTicket = null;
    const columnsList = ['todo', 'review', 'bug_fixing', 'done'];
    
    for (const col of columnsList) {
      const pages = columnSWRs[col]?.data || [];
      for (const page of pages) {
        if (page.data) {
          const ticket = page.data.find(t => t._id === id);
          if (ticket) { foundTicket = ticket; break; }
        }
      }
      if (foundTicket) break;
    }

    // 2. Fallback: Fetch from API
    if (!foundTicket) {
      try {
        const res = await api.get(`/tickets/${id}`);
        foundTicket = res.data?.data || res.data;
      } catch (err) {
        // Failed to fetch ticket for focus
        return;
      }
    }

    if (foundTicket) {
      startTransition(() => {
        setSelectedTicket(foundTicket);
        setEvidenceUrl(null);
      });
      window.focus();
    }
  }, [columnSWRs]);

  // --- EXTERNAL SYNC (Focus Tab / Single Tab Reuse) ---





  const handleLoadMore = useCallback((colId) => {
    const swr = columnSWRsRef.current[colId];
    if (swr) swr.setSize(swr.size + 1);
  }, []);

  const updateTicketInList = useCallback((updatedTicket) => {
    if (!updatedTicket || !updatedTicket._id) return;
    const id = String(updatedTicket._id);

    // Optimistic update via mutate on all columns
    Object.entries(columnSWRsRef.current).forEach(([columnId, swr]) => {
      if (!swr || typeof swr.mutate !== 'function') return;

      swr.mutate((current) => {
        if (!current) return current;

        // Calculate if ticket SHOULD be in this column using robust logic
        const belongsHere = isTicketInColumn(updatedTicket, columnId, showUiImprovements, hasUxMembers);

        return current.map((page, index) => {
          const existingIndex = (page.data || []).findIndex(t => String(t?._id) === id);
          const isFirstPage = index === 0;

          if (existingIndex !== -1) {
            // Ticket was in this column
            if (belongsHere) {
              // Still belongs here -> Update properties
              const newData = [...page.data];
              newData[existingIndex] = { ...updatedTicket };
              return { ...page, data: newData };
            } else {
              // No longer belongs here -> Remove it and decrement total
              const prevTotal = page.pagination?.total ?? page.data?.length ?? 0;
              return {
                ...page,
                pagination: isFirstPage ? {
                  ...page.pagination,
                  total: Math.max(0, prevTotal - 1)
                } : page.pagination,
                data: page.data.filter(t => String(t?._id) !== id)
              };
            }
          } else if (belongsHere && isFirstPage) {
            // Ticket didn't exist in this column but now belongs here -> Add and increment total
            const limit = page.pagination?.limit || page.limit || 20;
            const prevTotal = page.pagination?.total ?? page.data?.length ?? 0;

            return {
              ...page,
              pagination: {
                ...page.pagination,
                total: prevTotal + 1
              },
              data: [updatedTicket, ...(page.data || [])].slice(0, limit)
            };
          }

          return page;
        });
      }, false);
    });

    // Update global stats and counts
    if (selectedSprint && selectedSprint !== 'all') {
      mutateStats();
    }
    mutateCounts();

    setSelectedTicket(updatedTicket);
  }, [showUiImprovements, hasUxMembers, mutateStats, mutateCounts, selectedSprint]);

    const filteredTickets = React.useMemo(() => {
      const activeSprints = sprints.filter(s => s.status !== 'closed' && s.status !== 'completed').map(s => s._id);

      return tickets.filter(t => {
      if (!t) return false;

      // Exclusive Filter: UI Improvements vs Functional Tickets
      if (showUiImprovements) {
         if (!hasUxMembers || !t.isUiImprovement) return false;
      } else {
         // Only exclude if there are UX members
         if (hasUxMembers && t.isUiImprovement) return false;
      }


      // Filter out tickets from closed/completed sprints ONLY if we are not explicitly selecting "all" or specific sprint
      if (t.sprintId && 
          !activeSprints.map(s => String(s)).includes(String(t.sprintId)) && 
          selectedSprint !== 'all' && 
          String(selectedSprint) !== String(t.sprintId)) return false;

      // Bugs-only filter
      if (filterBugsOnly) {
        const isBug = t.type === 'bug' || t.status === 'bug_fixing' || t.originalStatus === 'bug_fixing';
        if (!isBug) return false;
      }

      // Priority filter
      if (selectedPriority !== 'all' && t.priority !== selectedPriority) return false;

      // Tags filter
      if (selectedTags !== 'all') {
        const hasTag = t.focusAreas && t.focusAreas.includes(selectedTags);
        if (!hasTag) return false;
      }

      // Creator filter
      if (selectedCreator !== 'all') {
        const tCreator = t.creator;
        const creatorId = String(tCreator?._id || tCreator?.id || (typeof tCreator === 'string' ? tCreator : ''));
        const creatorEmail = String(tCreator?.email || '').toLowerCase();
        const target = selectedCreator.toLowerCase();

        if (creatorId !== selectedCreator && creatorEmail !== target) return false;
      }

      // Date Range Filter
      if (selectedDateRange.type !== 'all') {
        const ticketDate = new Date(t.createdAt);
        const now = new Date();

        if (selectedDateRange.type === 'today') {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (ticketDate < today) return false;
        } else if (selectedDateRange.type === 'week') {
          const lastWeek = new Date();
          lastWeek.setDate(now.getDate() - 7);
          if (ticketDate < lastWeek) return false;
        } else if (selectedDateRange.type === 'month') {
          const lastMonth = new Date();
          lastMonth.setMonth(now.getMonth() - 1);
          if (ticketDate < lastMonth) return false;
        } else if (selectedDateRange.type === 'custom') {
          if (selectedDateRange.start) {
            const start = new Date(selectedDateRange.start);
            start.setHours(0, 0, 0, 0);
            if (ticketDate < start) return false;
          }
          if (selectedDateRange.end) {
            const end = new Date(selectedDateRange.end);
            end.setHours(23, 59, 59, 999);
            if (ticketDate > end) return false;
          }
        }
      }

      // Search filter
      if (debouncedSearchQuery) {
        const q = debouncedSearchQuery.toLowerCase().trim();
        const cleanQ = q.replace(/^vg-/, '').replace(/^#/, '');
        const ticketKey = String(t.key || '').toLowerCase();
        const ticketId = String(t._id || '').toLowerCase();
        const ticketIdSuffix = ticketId.slice(-6);

        if (!(t.title?.toLowerCase().includes(q) ||
          ticketKey.includes(q) ||
          ticketKey.includes(cleanQ) ||
          t.message?.toLowerCase().includes(q) ||
          ticketIdSuffix.includes(q) ||
          ticketIdSuffix.includes(cleanQ) ||
          ticketId.includes(q) ||
          ticketId.includes(cleanQ) ||
          t.creator?.name?.toLowerCase().includes(q) ||
          t.assignee?.name?.toLowerCase().includes(q) ||
          t.resolver?.name?.toLowerCase().includes(q))) return false;
      }

      // Sprint filter
      if (selectedSprint === 'all') return true;
      
      // If no sprint is selected, and we have active sprints in the state,
      // the Board should be showing the "implicitly active" one (handled by auto-selection above),
      // but as a fallback/safety, if selectedSprint IS null, we show backlog tickets.
      if (selectedSprint === null || selectedSprint === 'backlog') return !t.sprintId;
      
      return String(t.sprintId?._id || t.sprintId) === String(selectedSprint);
    }).map(t => {
      // Map SDK/Legacy statuses to canonical board columns using the SAME HIERARCHY as the backend (server.js:3476)
      const s = String(t.status || '').toLowerCase();
      let canonicalStatus = 'todo';

      // 1. Review Hierarchy (Highest)
      if (s === 'ready_for_review' || s === 'review') {
        canonicalStatus = 'review';
      }
      // 2. Done Hierarchy
      else if (s === 'done' || s === 'completed' || s === 'resolved') {
        canonicalStatus = 'done';
      }
      // 3. Bug Fixing Hierarchy
      else if (s === 'bug_fixing' || t.originalStatus === 'bug_fixing' || t.type === 'bug') {
        canonicalStatus = 'bug_fixing';
      }
      // 4. Fallback (Todo)
      else {
        canonicalStatus = 'todo';
      }

      return { ...t, boardColumn: canonicalStatus, title: t.title || t.message || t('untitled') || 'Untitled' };
    }).filter(t => t !== null).sort((a, b) => (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0));
  }, [tickets, selectedSprint, selectedPriority, selectedTags, selectedCreator, debouncedSearchQuery, sprints, t, filterBugsOnly, selectedDateRange, showUiImprovements, hasUxMembers]);


  const ticketsByStatus = React.useMemo(() => {
    const groups = { todo: [], review: [], bug_fixing: [], done: [] };
    filteredTickets.forEach(t => {
      if (groups[t.boardColumn]) groups[t.boardColumn].push(t);
    });
    return groups;
  }, [filteredTickets]);

  const totalTicketsCount = useMemo(() => {
    if (selectedSprint === 'backlog' || selectedSprint === null) {
      return dashboardCounts?.backlog ?? 0;
    }
    if (selectedSprint && selectedSprint !== 'all') {
      return dashboardCounts?.sprint ?? 0;
    }
    // Fallback for 'all' or loading state
    return sprintStats?.totalTickets ?? tickets.length;
  }, [dashboardCounts, selectedSprint, sprintStats?.totalTickets, tickets.length]);

  const hiddenCount = Math.max(0, totalTicketsCount - filteredTickets.length);
  const isFiltered = (selectedPriority !== 'all') || (selectedTags !== 'all') || (selectedCreator !== 'all') || (!!debouncedSearchQuery) || (selectedSprint !== 'all' && selectedSprint !== null) || (selectedDateRange.type !== 'all');


  const isLoading = !todoSWR.data && !reviewSWR.data && !bugFixingSWR.data && !doneSWR.data && !!activeProjectId;

  const ticketsInSelectedSprint = React.useMemo(() => {
    if (!selectedSprint || tickets.length === 0) return [];

    // With server-side filtering, we mostly trust 'tickets' but we still double check sprint
    return tickets.filter(t => {
      if (!t) return false;
      if (selectedSprint === 'all') return true;
      if (selectedSprint === null) return !t.sprintId;
      return String(t.sprintId) === String(selectedSprint);
    });
  }, [tickets, selectedSprint]);

  const sprintHealth = React.useMemo(() => {
    if (!activeProjectId || isLoading) return null;

    if (selectedSprint === 'all' || !selectedSprint) {
      // Return a project-level summary health
      if (!sprintStats && tickets.length === 0) return null;

      const total = sprintStats?.bugStats?.total || tickets.length || 0;
      const resolved = sprintStats?.bugStats?.resolved || tickets.filter(t => t.status === 'done').length || 0;
      const score = total > 0 ? (resolved / total) * 100 : 100;

      return {
        score,
        status: score > 70 ? 'healthy' : (score > 30 ? 'warning' : 'critical'),
        label: t('projectOverview') || 'Project Overview',
        isGlobal: true
      };
    }

    const currentSprint = sprints.find(s => s._id === selectedSprint);
    if (!currentSprint) return null;

    return calculateSprintHealth(currentSprint, sprintStats || ticketsInSelectedSprint, t);
  }, [selectedSprint, ticketsInSelectedSprint, sprints, t, isLoading, sprintStats, tickets, activeProjectId]);



  if (filteredProjects.length === 0 && isInitialized && !loading && !activeProjectId) {
    return (
      <EmptyState
        icon={Database}
        title={t('noProjectsInWorkspace')}
        description={t('noProjectsInWorkspaceDesc')}
        actionLabel={t('goProjects')}
        onAction={() => navigate('/projects')}
        className="h-[calc(100vh-160px)]"
      />
    );
  }

  if (!activeProjectId && filteredProjects.length > 0) {
    return (
      <EmptyState
        icon={Zap}
        title={t('selectProjectTitle')}
        description={t('selectProjectDesc')}
        className="h-[calc(100vh-160px)]"
      />
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden board-page-root">
      <header className="flex-none pt-10 px-10 mb-4 flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-4xl font-display font-black text-slate-100 flex items-center gap-4">
            <Columns className="text-primary" size={32} /> {t('board')}
            
            {hasUxMembers && (
              <motion.button 
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowUiImprovements(!showUiImprovements)}
                className={`ml-4 flex items-center gap-3 px-5 py-2.5 rounded-2xl transition-all duration-500 border relative overflow-hidden group ${
                  showUiImprovements 
                  ? 'bg-indigo-500/20 border-indigo-500/40 text-indigo-300 shadow-[0_0_25px_rgba(99,102,241,0.25)]' 
                  : 'bg-slate-900/40 border-white/5 text-slate-500 hover:border-white/10 hover:bg-slate-900/60'
                }`}
              >
                {/* Background Glow Effect */}
                <div className={`absolute inset-0 transition-opacity duration-500 ${showUiImprovements ? 'opacity-100' : 'opacity-0'}`}>
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-indigo-400 to-transparent opacity-50" />
                  <div className="absolute -inset-[100%] bg-[radial-gradient(circle_at_50%_0%,rgba(99,102,241,0.15),transparent_70%)]" />
                </div>

                <div className="relative z-10 flex items-center gap-2.5">
                  <div className={`p-1.5 rounded-lg transition-colors duration-500 ${showUiImprovements ? 'bg-indigo-500/20 text-indigo-400' : 'bg-white/5 text-slate-600 group-hover:text-slate-400'}`}>
                     <Sparkles size={16} className={showUiImprovements ? 'animate-pulse' : ''} />
                  </div>
                  <span className={`text-[11px] font-black uppercase tracking-[0.1em] transition-colors duration-500 ${showUiImprovements ? 'text-indigo-200' : 'text-slate-500'}`}>
                    {t('uiImprovements')}
                  </span>
                  
                  {/* Subtle Indicator dot */}
                  <div className={`w-1.5 h-1.5 rounded-full transition-all duration-500 ${showUiImprovements ? 'bg-indigo-400 scale-100 shadow-[0_0_8px_rgba(129,140,248,1)]' : 'bg-slate-700 scale-75'}`} />
                </div>
              </motion.button>
            )}
          </h1>
          <div className="flex items-center gap-5 mt-6">


            {(() => {
              const availableSprints = sprints.filter(s => s.status !== 'closed' && s.status !== 'completed');
              // Logic separated to avoid full header return null

              const formatDate = (dateStr) => {
                if (!dateStr) return '';
                const d = new Date(dateStr);
                return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
              };

              return (
                <>
                  {isLoading ? (
                    <HeaderComponentSkeleton width={224} />
                  ) : (
                    <CustomDropdown
                      className="w-56"
                      options={[
                        { value: 'all', label: t('allSprints'), icon: ListTodo },
                        { value: null, label: t('noSprint'), icon: MinusCircle },
                        ...availableSprints.map(s => {
                          const subText = s.status === 'active' ? t('active') : (s.startDate && s.endDate ? formatDate(s.startDate) : '');
                          return {
                            value: s._id,
                            label: (
                              <div className="flex items-center gap-2 overflow-hidden">
                                <span className="truncate">{s.name}</span>
                                {subText && (
                                  <span className="text-[10px] opacity-40 font-medium shrink-0">
                                    • {subText}
                                  </span>
                                )}
                              </div>
                            ),
                            activeColor: s.status === 'active' ? '#10b981' : undefined
                          };
                        })
                      ]}
                      value={selectedSprint}
                      onChange={setSelectedSprint}
                      icon={Play}
                      placeholder={t('selectSprint')}
                    />
                  )}


                  {(() => {
                    const sprint = sprints.find(s => s._id === selectedSprint);

                    // Use a "Project Summary" sprint if we're in "all" mode or sprint not found
                    const baseSprint = sprint || {
                      _id: 'all',
                      name: t('projectOverview') || 'Project Overview',
                      status: 'active',
                      totalBugCount: sprintStats?.bugStats?.total || tickets.length,
                      resolvedBugCount: sprintStats?.bugStats?.resolved || tickets.filter(t => t.status === 'done').length,
                      resolvedBugPoints: sprintStats?.bugStats?.points || 0
                    };

                    if (isLoading) return <HeaderComponentSkeleton width={180} />;

                    const totalCount = sprintStats?.bugStats?.total || baseSprint.totalBugCount || 0;
                    // Don't hide the card if we're in 'all' mode, show what we have
                    if (totalCount === 0 && !sprintStats && selectedSprint !== 'all') return <div className="w-[180px]" />; // Reserve space

                    const augmentedSprint = {
                      ...baseSprint,
                      resolvedCount: showUiImprovements ? (sprintStats?.columnCounts?.done || 0) : (sprintStats?.bugStats?.resolved ?? (baseSprint.resolvedBugCount || 0)),
                      totalCount: showUiImprovements ? (sprintStats?.totalTickets || 0) : (sprintStats?.bugStats?.total || baseSprint.totalBugCount || 0),
                      resolvedBugPoints: showUiImprovements ? (sprintStats?.points?.resolved || 0) : (sprintStats?.bugStats?.points ?? (baseSprint.resolvedBugPoints || 0))
                    };

                    return (
                      <BugStatCard
                        sprint={augmentedSprint}
                        t={t}
                        activeProject={teams.find(p => p._id === activeProjectId)}
                        isActive={showUiImprovements ? true : filterBugsOnly}
                        onToggle={() => !showUiImprovements && setFilterBugsOnly(prev => !prev)}
                        collisionRegistry={collisionRegistry}
                        isUiMode={showUiImprovements}
                      />
                    );
                  })()}

                  {isLoading ? (
                    <HeaderComponentSkeleton width={120} />
                  ) : sprintHealth && (
                    (() => {
                      const sprint = sprints.find(s => s._id === selectedSprint);
                      const endDate = sprint?.endDate;
                      let daysRemaining = undefined;

                      if (endDate) {
                        const end = new Date(endDate);
                        const now = new Date();
                        const diff = end.getTime() - now.getTime();
                        daysRemaining = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
                      }

                      return (
                        <ScrumTrafficLight
                          health={sprintHealth}
                          t={t}
                          endDate={endDate}
                          daysRemaining={daysRemaining}
                        />
                      );
                    })()
                  )}
                </>
              );
            })()}

            <div className="flex items-center pl-6 flex-none">
              <button
                onClick={() => setIsFilterModalOpen(true)}
                className={`flex items-center justify-center w-11 h-11 transition-all relative flex-none ${isFiltered
                    ? 'bg-transparent text-primary'
                    : 'bg-transparent text-slate-400 hover:text-white'
                  }`}
              >
                <Filter size={18} />
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <input
              type="text"
              id="board-search"
              name="search"
              placeholder={t('findInBoard')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-xl py-2 pl-9 pr-4 text-xs focus:outline-none focus:border-primary/50 transition-all w-48"
            />
          </div>
        </div>
      </header>

      {/* Limited View Banner */}
      <AnimatePresence>
        {isLimited && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="px-10 pt-4 pb-2 overflow-hidden"
          >
            <div
              onClick={() => navigate('/settings/subscription')}
              className="group p-4 rounded-2xl bg-primary/10 border border-primary/20 flex flex-col md:flex-row items-center justify-between gap-4 cursor-pointer hover:bg-primary/20 transition-all shadow-xl shadow-primary/5"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center text-primary border border-primary/30">
                  <Lock size={20} />
                </div>
                <div className="space-y-0.5 text-center md:text-left">
                  <h4 className="text-sm font-bold text-white uppercase tracking-wider">{t('limited_view_title')}</h4>
                  <p className="text-xs text-secondary">{t('limited_view_desc')}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-primary bg-primary/10 px-4 py-2 rounded-xl group-hover:bg-primary group-hover:text-surface-dark transition-all">
                {t('subscribe_now')}
                <ArrowRight size={14} />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>



      <div className="flex-1 board-container min-h-0 px-10 relative">
        {/* Global bottom fade overlay - fixed to board bottom */}
        <div
          className="absolute bottom-0 left-0 right-0 h-28 pointer-events-none z-20"
          style={{ background: 'linear-gradient(to bottom, transparent, #080c14)' }}
        />
        <div className="flex h-full overflow-hidden select-none border-t border-white/5 bg-transparent relative">
          {debouncedSearchQuery && filteredTickets.length === 0 && !isLoading ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-12 text-center space-y-4">
              <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center text-slate-500/20 border border-white/5">
                <Search size={40} />
              </div>
              <div className="max-w-md">
                <h4 className="text-slate-200 font-bold text-lg">{t('noSearchResults')}</h4>
                <p className="text-slate-500 text-sm mt-1">
                  {t('noSearchResultsDesc').replace('{{query}}', debouncedSearchQuery)}
                </p>
              </div>
            </div>
          ) : (
            <AnimatePresence mode="wait">
              {isLoading ? (
                <motion.div
                  key="board-skeleton"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex h-full w-full"
                >
                  {STATUS_COLUMNS.map(col => (
                    <ColumnSkeleton key={`skeleton-${col.id}`} id={col.id} />
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  key="board-content"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex h-full w-full"
                >
                  {STATUS_COLUMNS.map(col => {
                    const swr = columnSWRs[col.id];
                    return (
                      <Column
                        key={col.id}
                        id={col.id}
                        tickets={ticketsByStatus[col.id] || EMPTY_ARRAY}
                        onOpenTicket={handleOpenTicket}
                        onLoadMore={handleLoadMore}
                        hasMore={swr?.data ? swr.data[swr.data.length - 1]?.data?.length === 5 : false}
                        isLoadingMore={swr?.isValidating && swr?.size > 1}
                        totalCount={swr?.data?.[0]?.pagination?.total ?? sprintStats?.columnCounts?.[col.id]}
                        registerTicket={registerTicket}
                        isStatsLoading={isStatsValidating || (swr.isValidating && !swr.data)}
                      />
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </div>
      </div>

      {createPortal(
        <AnimatePresence mode='wait'>
          {selectedTicket && (
            <TicketModal
              key="board-ticket-modal"
              ticket={selectedTicket}
              onClose={handleCloseTicket}
              onUpdate={updateTicketInList}
              mutateTickets={mutateTickets}
              // Pass the board-level state
              evidenceCache={evidenceCache}
              setEvidenceCache={setEvidenceCache}
              onOpenEvidence={handleOpenEvidence}
            />
          )}
        </AnimatePresence>,
        document.body
      )}

      {createPortal(
        <AnimatePresence>
          {isEvidenceOpen && (
            <motion.div
              key="evidence-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-20 bg-slate-950/40 backdrop-blur-[120px]"
              onClick={() => setEvidenceUrl(null)}
            >
              {/* Floating Close Button */}
              <button
                onClick={() => setEvidenceUrl(null)}
                className="absolute top-8 right-8 w-14 h-14 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-3xl border border-white/20 text-white transition-all flex items-center justify-center z-[10000] group shadow-2xl"
              >
                <X size={28} className="group-hover:rotate-90 transition-transform duration-500" />
              </button>

              <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 50 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 50 }}
                transition={{ type: "spring", damping: 30, stiffness: 250 }}
                className="relative max-w-7xl max-h-full flex items-center justify-center"
                onClick={(e) => e.stopPropagation()}
              >
                <div className={`relative rounded-[48px] overflow-hidden border border-white/10 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.8),0_0_80px_rgba(99,102,241,0.1)] transition-all duration-700 bg-slate-900/20 ${isFullEvidenceLoading ? 'w-[90vw] max-w-6xl aspect-video skeleton-shimmer z-20' : 'w-auto h-auto'}`}>
                  {isFullEvidenceLoading && (
                    <div className="absolute inset-0 flex items-center justify-center p-12">
                      <motion.div
                        animate={{
                          scale: [1, 1.05, 1],
                          opacity: [0.6, 1, 0.6]
                        }}
                        transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                        className="flex flex-col items-center gap-8 w-full max-w-md"
                      >
                        <div className="w-28 h-28 rounded-[40px] bg-white/5 border border-white/10 flex items-center justify-center text-white/40 shadow-2xl backdrop-blur-md">
                          <Camera size={56} strokeWidth={1} />
                        </div>
                        <div className="flex flex-col items-center gap-4 w-full">
                          <span className="text-[12px] font-black uppercase tracking-[0.5em] text-white/50 italic text-center w-full">Invocando Evidencia</span>
                        </div>
                      </motion.div>
                    </div>
                  )}
                  <img
                    src={evidenceUrl?.includes('cloudinary') ? evidenceUrl.replace('/upload/', '/upload/f_auto,q_auto:best,w_1920,c_limit/') : evidenceUrl}
                    alt="Evidence"
                    onLoad={() => setIsFullEvidenceLoading(false)}
                    className={`max-w-full max-h-[85vh] object-contain pointer-events-none select-none transition-all duration-1000 ${isFullEvidenceLoading ? 'opacity-0 scale-110 blur-3xl' : 'opacity-100 scale-100 blur-0'}`}
                  />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        t={t}
        showPriority={true}
        showDateRange={true}
        showTags={true}
        showCreator={true}
        selectedPriority={selectedPriority}
        selectedTags={selectedTags}
        selectedCreator={selectedCreator}
        selectedMemberType={selectedMemberType}
        selectedDateRange={selectedDateRange}
        onApply={({ priority, tags, creator, memberType, dateRange }) => {
          setSelectedPriority(priority);
          setSelectedTags(tags);
          setSelectedCreator(creator);
          setSelectedMemberType(memberType || 'creator');
          setSelectedDateRange(dateRange);
        }}
        members={(() => {
          const list = [...(activeProject?.members || [])];
          if (user && !list.find(m => (m._id || m.id) === user.id || m.email === user.email)) {
            list.unshift({
              _id: user.id,
              name: user.name,
              email: user.email,
              avatar: user.avatar,
              role: user.role || 'pm'
            });
          }
          return list;
        })()}
      />
    </div>
  );
};

export default Board;
